import React, { useState, useEffect, useRef } from 'react';
import FormularioMateriaPrima from './FormularioMateriaPrima';
import BASE_URL from '../utils/api';
import BotaoVoltarTopo from '../components/BotaoVoltarTopo';
import { hojeBrasilia } from '../utils/dataBrasilia';
import { ehPrincipal, salvarPrincipal } from '../utils/responsavelPrincipal';
import PopupNovaPessoa from '../components/PopupNovaPessoa';

// Função utilitária para chamadas da API com fallback
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const urls = [
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`,
    `${BASE_URL}/${endpoint}`
  ];

  let lastError;

  for (const url of urls) {
    let shouldNotify = false;
    let notifyResultado = null;
    let notifyOptions = null;
    try {
      console.log(`Tentando ${method} em: ${url}`);
      const token = localStorage.getItem('authToken');
      const config = {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      };
      
      if (data && (method === 'POST' || method === 'PUT')) {
        config.body = JSON.stringify(data);
        console.log(`Payload enviado para ${method}:`, data);
      }

      const response = await fetch(url, config);
      console.log(`Resposta recebida - Status: ${response.status}, OK: ${response.ok}`);
      
      // Aceitar códigos de status de sucesso (200-299)
      if (response.ok || (response.status >= 200 && response.status < 300)) {
        console.log(`SUCESSO em: ${url} - Status: ${response.status}`);
        
        // Para DELETE ou status 204 (No Content)
        if (method === 'DELETE' || response.status === 204) {
          return { success: true, status: response.status };
        }
        
        // Verificar se há conteúdo para fazer parse JSON
        const contentType = response.headers.get('Content-Type') || '';
        console.log(`Content-Type: ${contentType}`);
        
        // Se é um PDF, tratar de forma especial
        if (contentType.includes('application/pdf')) {
          console.log(`PDF detectado na resposta`);
          const pdfBlob = await response.blob();
          return { 
            success: true, 
            status: response.status, 
            type: 'application/pdf',
            data: pdfBlob,
            isPdf: true
          };
        } else if (contentType.includes('application/json')) {
          try {
            const jsonData = await response.json();
            console.log(`JSON parseado com sucesso:`, jsonData);
            
            // Verificar se o JSON contém dados de PDF em base64
            if (jsonData.arquivoPDFOrcamento || jsonData.ArquivoPDFOrcamento || jsonData.pdf) {
              console.log(`PDF em base64 detectado no JSON`);
              return {
                ...jsonData,
                isPdf: true,
                type: 'application/pdf'
              };
            }
            
            return jsonData;
          } catch (jsonError) {
            console.log(`Erro ao parsear JSON, mas operação foi bem-sucedida - Status: ${response.status}`);
            return { success: true, status: response.status };
          }
        } else {
          // Resposta sem JSON (ex: texto simples, HTML, etc)
          try {
            const textData = await response.text();
            console.log(`Resposta texto:`, textData);
            return { success: true, status: response.status, data: textData };
          } catch (textError) {
            console.log(`Erro ao ler resposta como texto, mas operação foi bem-sucedida`);
            return { success: true, status: response.status };
          }
        }
      }
      
      // Se chegou aqui, a resposta não foi bem-sucedida
      console.log(`Falha em: ${url} - Status: ${response.status}`);
      
      // Tentar extrair detalhes do erro
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorText = await response.text();
        if (errorText) {
          errorMessage = `HTTP ${response.status}: ${errorText}`;
          console.error(`Detalhes do erro:`, errorText);
        }
      } catch (readError) {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      
      lastError = new Error(errorMessage);
      
    } catch (error) {
      console.log(`Erro de rede em: ${url} - ${error.message}`);
      lastError = error;
      continue;
    }
  }
  
  // Se chegou aqui, nenhuma URL funcionou
  const finalError = new Error(`Nenhuma URL da API funcionou para ${method} ${endpoint}. Último erro: ${lastError?.message || 'Desconhecido'}`);
  console.error('Erro final da API:', finalError.message);
  throw finalError;
};

// Cada serviço do orçamento tem os próprios responsáveis e matérias-primas.
// `chave` identifica o serviço na tela (o Id do banco só existe depois de salvar).
let contadorChaveServico = 0;
const novaChaveServico = () => `servico-${++contadorChaveServico}`;
const novoResponsavelServico = () => ({ pessoaId: '', horasPrevistas: '', horasReais: '' });
const novoServicoOrcamento = () => ({
  chave: novaChaveServico(),
  titulo: '',
  descricao: '',
  valorServico: 0,
  quantidade: 1,
  responsaveis: [novoResponsavelServico()]
});
const idResponsavel = (r) => r.id || r.responsaveisId || r.ResponsaveisId;

// Campo numérico com a unidade dentro dele: "R$" antes do número ou "%" logo depois do número digitado.
// O usuário digita só o número; a unidade não faz parte do valor.
// Uma camada por cima do input usa a mesma fonte e o mesmo recuo do texto: assim o "%" acompanha a
// largura do número (texto invisível com o mesmo valor empurra o "%") e o "R$" fica na mesma linha.
const RECUO_TEXTO = 10;   // padding horizontal do texto no input
const RECUO_PREFIXO = 36; // espaço reservado para o "R$"
const CampoComUnidade = ({ prefixo, sufixo, corBorda = '#ccc', value, placeholder, ...props }) => {
  const texto = value === undefined || value === null || value === '' ? (placeholder || '') : String(value);
  const estiloTexto = { fontFamily: 'inherit', fontSize: 14, lineHeight: 'normal' };
  return (
    <div className="campo-com-unidade" style={{ position: 'relative', display: 'inline-block', width: 200 }}>
      <input
        type="number"
        value={value}
        placeholder={placeholder}
        {...props}
        style={{
          ...estiloTexto,
          width: '100%', boxSizing: 'border-box', padding: `8px ${RECUO_TEXTO}px`,
          paddingLeft: prefixo ? RECUO_PREFIXO : RECUO_TEXTO,
          borderRadius: 4, border: `1px solid ${corBorda}`, background: '#fff'
        }}
      />
      <div
        aria-hidden="true"
        style={{
          ...estiloTexto,
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
          // +1px da borda do input, para começar exatamente onde o texto começa
          paddingLeft: RECUO_TEXTO + 1,
          pointerEvents: 'none', overflow: 'hidden', whiteSpace: 'pre'
        }}
      >
        {prefixo && <span style={{ color: '#555', fontWeight: 600, width: RECUO_PREFIXO - RECUO_TEXTO }}>{prefixo}</span>}
        {sufixo && (
          <>
            <span style={{ visibility: 'hidden' }}>{texto}</span>
            <span style={{ color: '#555', fontWeight: 600, marginLeft: 2 }}>{sufixo}</span>
          </>
        )}
      </div>
      <style>{`
        /* Sem as setinhas do campo numérico, que ficariam por cima da unidade */
        .campo-com-unidade input::-webkit-outer-spin-button,
        .campo-com-unidade input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .campo-com-unidade input[type=number] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
};
const arredondarValor = (v) => Math.round(v * 100) / 100;

function FormularioOrcamento({ orcamentoParaEditar, clienteSelecionado, oportunidadeSelecionada, onSalvarSucesso, onCancelar, onCompartilhar }) {
  // Estado do formulário principal
  const [form, setForm] = useState({
    oportunidadeId: '',
    id_cliente: '',
    status: 'Rascunho',
    dataCriacao: hojeBrasilia(),
    descricaoOrcamento: '',
    validadeOrcamento: '',
    prazo_garantia: 12, // Padrão de 12 meses
    desconto: '',
    valor: 0, // Será calculado automaticamente
    custoOrcado: 0 // Custo orçado para controle interno
  });

  // Percentual de lucro aplicado sobre o valor base do orçamento
  const [adicionalLucro, setAdicionalLucro] = useState('');
  // Desconto informado em R$ (form.desconto) ou em % (descontoPercentual)
  const [tipoDesconto, setTipoDesconto] = useState('valor');
  const [descontoPercentual, setDescontoPercentual] = useState('');
  // Custo do orçamento (sem lucro e sem desconto) — base do percentual de lucro e campo Valor do payload
  const [valorBaseOrcamento, setValorBaseOrcamento] = useState(0);

  // Estado dos serviços
  const [servicos, setServicos] = useState(() => [novoServicoOrcamento()]);

  // Estado das condições de pagamento
  const [condicoesPagamento, setCondicoesPagamento] = useState([
    { descricao: '' }
  ]);

  const [clientes, setClientes] = useState([]);
  const [oportunidades, setOportunidades] = useState([]);
  const [loading, setLoading] = useState(false);
  // Duplicar orçamento: null (fechado) | 'confirmar' | 'duplicando' | { id } (sucesso) | { erro }
  const [duplicacao, setDuplicacao] = useState(null);

  const duplicarOrcamento = async () => {
    const orcId = orcamentoParaEditar?.id || orcamentoParaEditar?.Id;
    if (!orcId) return;
    setDuplicacao('duplicando');
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${BASE_URL}/Orcamento/${orcId}/duplicar`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!resp.ok) throw new Error(await resp.text() || 'Erro ao duplicar o orçamento.');
      const novo = await resp.json();
      setDuplicacao({ id: novo.id ?? novo.Id });
    } catch (e) {
      setDuplicacao({ erro: e.message || 'Erro ao duplicar o orçamento.' });
    }
  };

  const fecharDuplicacao = () => {
    const sucesso = duplicacao && typeof duplicacao === 'object' && duplicacao.id;
    setDuplicacao(null);
    // Fecha o formulário e recarrega a lista, sem perguntar sobre download
    if (sucesso && onSalvarSucesso) onSalvarSucesso({ id: duplicacao.id }, { uploadedPdf: true });
  };
  const [error, setError] = useState('');
  const [mostrarSucesso, setMostrarSucesso] = useState(false);
  const [botaoDesabilitado, setBotaoDesabilitado] = useState(false);
  
  const [snapshotOriginal, setSnapshotOriginal] = useState(null); // snapshot para validar aprovação

  // ── Responsáveis do orçamento ──
  const [pessoas, setPessoas] = useState([]); // lista de pessoas disponíveis (GET /api/Pessoas)
  // Novos responsáveis ficam em servicos[i].responsaveis; aqui só os já salvos (edição)
  const [responsaveisExistentes, setResponsaveisExistentes] = useState([]);
  // Responsável principal do orçamento entre os NOVOS (gravado no Salvar): "chaveDoServico:indice" | null.
  // Um só por orçamento; os já salvos são marcados direto (botão ⭐).
  const [principalNovo, setPrincipalNovo] = useState(null);
  // Serviço em que o pop-up "Adicionar nova pessoa" foi aberto (índice) | null
  const [novaPessoaServico, setNovaPessoaServico] = useState(null);

  // ── Matérias-Primas: um FormularioMateriaPrima por serviço ──
  const refsMateriaPrimaServico = useRef({}); // chave do serviço → ref do componente
  const [valorMateriaPrimaPorServico, setValorMateriaPrimaPorServico] = useState({}); // chave → R$
  // Itens de orçamentos antigos, cadastrados antes de existir o vínculo com serviço

  // ── Serviços Terceiros do orçamento ──
  const [servicosTerceirosOrc, setServicosTerceirosOrc] = useState([{ descricao: '', valor: '', fornecedorId: '', tipoServicoId: '', dataExecucao: '', status: 'Pendente' }]);
  const [servicosTerceirosExistentes, setServicosTerceirosExistentes] = useState([]);
  // Serviço terceiro já salvo sendo editado na própria lista (índice + campos)
  const [stEditando, setStEditando] = useState(null);

  // Só os campos que o servidor grava (a lista traz também nomes de exibição que não podem voltar no envio)
  const dadosServicoTerceiro = (st) => ({
    id: st.id ?? st.Id,
    descricao: st.descricao ?? st.Descricao ?? null,
    valor: parseFloat(st.valor ?? st.Valor) || 0,
    fornecedorId: (st.fornecedorId ?? st.FornecedorId) || null,
    tipoServicoId: (st.tipoServicoId ?? st.TipoServicoId) || null,
    dataEntrega: (st.dataEntrega ?? st.DataEntrega) || null,
    status: st.status ?? st.Status ?? 'Pendente',
    producaoId: (st.producaoId ?? st.ProducaoId) || null,
    id_cliente: (st.id_cliente ?? st.Id_cliente) || null,
    tipoUsuarioId: (st.tipoUsuarioId ?? st.TipoUsuarioId) || null,
    orcamentoId: (st.orcamentoId ?? st.OrcamentoId) || null
  });

  const salvarEdicaoServicoTerceiro = async (st, idx) => {
    if (!stEditando.descricao.trim()) {
      alert('A descrição do serviço terceiro é obrigatória.');
      return;
    }
    const atualizado = {
      ...dadosServicoTerceiro(st),
      descricao: stEditando.descricao.trim(),
      valor: parseFloat(stEditando.valor) || 0,
      fornecedorId: parseInt(stEditando.fornecedorId) || null,
      tipoServicoId: parseInt(stEditando.tipoServicoId) || null,
      dataEntrega: stEditando.dataEntrega || null,
      orcamentoId: orcamentoParaEditar?.id || orcamentoParaEditar?.Id || null
    };
    try {
      await apiCall(`ServicosTerceiros/${atualizado.id}`, 'PUT', atualizado);
      setServicosTerceirosExistentes(prev => prev.map((item, i) => i === idx ? { ...item, ...atualizado } : item));
      setStEditando(null);
    } catch (e) {
      console.error('Erro ao editar serviço terceiro:', e);
      alert('Não foi possível salvar o serviço terceiro.');
    }
  };
  const [fornecedoresOrc, setFornecedoresOrc] = useState([]);
  const [tiposServicoOrc, setTiposServicoOrc] = useState([]);

  // ── Contrato vinculado ──
  const [temContrato, setTemContrato] = useState(false);

  // Orçamento aprovado é somente leitura — só pode ser aceito pelo link de aprovação
  const isReadOnly = !!(orcamentoParaEditar && form.status === 'Aprovado') || temContrato;

  // Status disponíveis
  const statusDisponiveis = [
    'Rascunho',
    'Enviado',
    'Em Análise',
    'Rejeitado',
    'Expirado'
  ];

  // Carregamento inicial
  useEffect(() => {
    console.log('🔄 useEffect acionado:', {
      orcamentoParaEditar: orcamentoParaEditar?.id,
      clienteSelecionado: clienteSelecionado?.id,
      oportunidadeSelecionada: oportunidadeSelecionada?.id
    });
    
    carregarDados();
    
    // Se há um orçamento para editar, carrega os dados
    if (orcamentoParaEditar) {
      console.log('📝 Iniciando carregamento de orçamento para edição');
      carregarOrcamentoParaEdicao();
    }
    
    // Se há cliente ou oportunidade pré-selecionados
    if (clienteSelecionado) {
      const clienteId = clienteSelecionado.id || clienteSelecionado.Id;
      console.log('🔄 Preenchendo cliente selecionado:', clienteId, clienteSelecionado.nome);
      setForm(prev => ({ ...prev, id_cliente: clienteId }));
    }
    
    if (oportunidadeSelecionada) {
      const oportunidadeId = oportunidadeSelecionada.id || oportunidadeSelecionada.Id;
      const clienteId = oportunidadeSelecionada.clienteId || oportunidadeSelecionada.ClienteId || clienteSelecionado?.id || clienteSelecionado?.Id;
      console.log('🔄 Preenchendo oportunidade selecionada:', oportunidadeId, 'para cliente:', clienteId);
      setForm(prev => ({ 
        ...prev, 
        oportunidadeId: oportunidadeId,
        id_cliente: clienteId 
      }));
    }
  }, [orcamentoParaEditar, clienteSelecionado, oportunidadeSelecionada]);

  // ── Custos ──
  // Responsável custa custo/hora × horas previstas
  const custoResponsavelNovo = (r) => {
    if (!r.pessoaId) return 0;
    const pessoa = pessoas.find(p => p.id == r.pessoaId);
    return (parseFloat(pessoa?.custoHora) || 0) * (parseFloat(r.horasPrevistas) || 0);
  };
  const custoResponsavelExistente = (r) =>
    (parseFloat(r.pessoaCustoHora ?? r.PessoaCustoHora ?? r.custoHora ?? r.CustoHora) || 0) * (parseFloat(r.horasPrevistas) || 0);

  const responsaveisExistentesDoServico = (servico) =>
    servico.id ? responsaveisExistentes.filter(r => (r.servicoOrcamentoId ?? r.ServicoOrcamentoId) === servico.id) : [];
  // Totais do serviço: responsáveis e matérias-primas (o custo vai para ValorServico, não aparece no PDF)
  const custoResponsaveisDoServico = (servico) => arredondarValor(
    servico.responsaveis.reduce((t, r) => t + custoResponsavelNovo(r), 0) +
    responsaveisExistentesDoServico(servico).reduce((t, r) => t + custoResponsavelExistente(r), 0)
  );
  const custoMateriasPrimasDoServico = (servico) => arredondarValor(valorMateriaPrimaPorServico[servico.chave] || 0);
  const custoDoServico = (servico) => arredondarValor(custoResponsaveisDoServico(servico) + custoMateriasPrimasDoServico(servico));

  // Custo orçado = serviços (responsáveis + matérias-primas de cada um) + serviços terceiros
  const custoCalculado = arredondarValor(
    servicos.reduce((t, s) => t + custoDoServico(s), 0) +
    servicosTerceirosOrc.reduce((t, st) => t + (parseFloat(st.valor) || 0), 0) +
    servicosTerceirosExistentes.reduce((t, st) => t + (parseFloat(st.valor ?? st.Valor) || 0), 0)
  );

  // Lucro: % sobre o custo. Desconto: em R$ ou em % sobre (custo + lucro); o banco guarda sempre em R$.
  const valorLucroCalculado = arredondarValor(custoCalculado * (parseFloat(adicionalLucro) || 0) / 100);
  const descontoReais = arredondarValor(tipoDesconto === 'percentual'
    ? (custoCalculado + valorLucroCalculado) * (parseFloat(descontoPercentual) || 0) / 100
    : (parseFloat(form.desconto) || 0));

  // Valor total = custo + lucro − desconto. Mesmo cálculo na criação e na edição.
  useEffect(() => {
    // Valor (banco) = custo; Lucro e Desconto salvos separados; preco_final = valor total
    setValorBaseOrcamento(custoCalculado);
    setForm(prev => ({ ...prev, custoOrcado: custoCalculado, valor: Math.max(0, arredondarValor(custoCalculado + valorLucroCalculado - descontoReais)) }));
  }, [custoCalculado, valorLucroCalculado, descontoReais]);

  // Carrega dados auxiliares (clientes e oportunidades)
  const carregarDados = async () => {
    try {
      setLoading(true);
      
      const [clientesResponse, oportunidadesResponse, pessoasResponse, fornecedoresResponse, tiposServicoResponse] = await Promise.all([
        apiCall('Cliente'),
        apiCall('Oportunidade'),
        apiCall('Pessoas'),
        apiCall('Fornecedor'),
        apiCall('TipoServico')
      ]);

      console.log('📊 Clientes carregados:', clientesResponse);
      console.log('📊 Oportunidades carregadas:', oportunidadesResponse);
      console.log('📊 Pessoas carregadas:', pessoasResponse);

      setClientes(clientesResponse || []);
      setOportunidades(oportunidadesResponse || []);
      setFornecedoresOrc(Array.isArray(fornecedoresResponse) ? fornecedoresResponse : []);
      setTiposServicoOrc(Array.isArray(tiposServicoResponse) ? tiposServicoResponse : []);

      // Mapear pessoas para select
      setPessoas(mapearPessoas(pessoasResponse));
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setError('Erro ao carregar dados necessários');
    } finally {
      setLoading(false);
    }
  };

  // Carrega orçamento para edição
  const carregarOrcamentoParaEdicao = async () => {
    try {
      setLoading(true);
      setError('');
      
      const orcamentoId = orcamentoParaEditar.id || orcamentoParaEditar.Id;
      console.log('🔄 Carregando orçamento completo para edição, ID:', orcamentoId);
      
      // 1. Buscar orçamento + coleções em endpoint unificado (se existir)
      console.log('📋 1/3 - Buscando dados completos (unificado) ...');
      let orcamentoCompleto = null;
      try {
        orcamentoCompleto = await apiCall(`Orcamento/${orcamentoId}`); // novo endpoint já retorna Servicos e Condicoes
      } catch (ePrimario) {
        console.warn('⚠️ Falha no endpoint unificado, tentando fallback simples:', ePrimario.message);
        orcamentoCompleto = await apiCall(`Orcamento/${orcamentoId}`); // mantém para compat
      }
      const orcamento = orcamentoCompleto.orcamento || orcamentoCompleto;
      
      // 2. Buscar serviços do orçamento (só se não vieram no unificado)
      console.log('🔧 2/3 - Buscando serviços (se necessário)...');
      let servicosCarregados = [];
      try {
        const servicosInline = orcamentoCompleto.Servicos || orcamentoCompleto.servicos;
        if (Array.isArray(servicosInline) && servicosInline.length) {
          console.log('✅ Serviços obtidos diretamente do endpoint unificado');
          servicosCarregados = servicosInline;
        } else {
          const servicosResponse = await apiCall(`ServicosOrcamento/orcamento/${orcamentoId}`);
          console.log('📥 Resposta dos serviços:', servicosResponse);
          if (servicosResponse && servicosResponse.servicos) {
            servicosCarregados = servicosResponse.servicos;
            console.log(`✅ ${servicosResponse.totalServicos} serviços carregados via novo formato:`, servicosCarregados);
          } else if (Array.isArray(servicosResponse)) {
            servicosCarregados = servicosResponse;
            console.log('✅ Serviços carregados via formato legado:', servicosCarregados);
          } else {
            console.log('⚠️ Formato de resposta inesperado para serviços');
            servicosCarregados = [];
          }
        }
      } catch (servicosError) {
        console.warn('⚠️ Erro ao carregar serviços:', servicosError);
        // Tenta usar serviços do orçamento completo como fallback
        servicosCarregados = orcamentoCompleto.servicos || orcamentoCompleto.Servicos || [];
      }
      
      // 3. Buscar condições de pagamento (só se não vieram no unificado)
      console.log('💳 3/3 - Buscando condições (se necessário)...');
      let condicoesCarregadas = [];
      try {
        const condicoesInline = orcamentoCompleto.Condicoes || orcamentoCompleto.condicoes;
        if (Array.isArray(condicoesInline) && condicoesInline.length) {
          console.log('✅ Condições obtidas diretamente do endpoint unificado');
          condicoesCarregadas = condicoesInline;
        } else {
          const condicoesResponse = await apiCall(`CondicoesPagamentoOrc/orcamento/${orcamentoId}`);
          console.log('📥 Resposta das condições:', condicoesResponse);
          if (condicoesResponse && condicoesResponse.condicoes) {
            condicoesCarregadas = condicoesResponse.condicoes;
            console.log(`✅ ${condicoesResponse.totalCondicoes} condições carregadas via novo formato:`, condicoesCarregadas);
          } else if (Array.isArray(condicoesResponse)) {
            condicoesCarregadas = condicoesResponse;
            console.log('✅ Condições carregadas via formato legado:', condicoesCarregadas);
          } else {
            console.log('⚠️ Formato de resposta inesperado para condições');
            condicoesCarregadas = [];
          }
        }
      } catch (condicoesError) {
        console.warn('⚠️ Erro ao carregar condições:', condicoesError);
        // Tenta usar condições do orçamento completo como fallback
        condicoesCarregadas = orcamentoCompleto.condicoes || orcamentoCompleto.Condicoes || [];
      }
      
      console.log('📊 Dados finais carregados:');
      console.log('  - Orçamento:', orcamento);
      console.log('  - Serviços:', servicosCarregados);
      console.log('  - Condições:', condicoesCarregadas);
      
      // Atualizar formulário principal com dados do orçamento
      const formData = {
        oportunidadeId: orcamento.oportunidadeId || orcamento.OportunidadeId || '',
        id_cliente: orcamento.id_cliente || orcamento.Id_cliente || orcamento.clienteId || orcamento.ClienteId || '',
        status: orcamento.status || orcamento.Status || 'Rascunho',
        dataCriacao: (orcamento.dataCriacao || orcamento.DataCriacao || '').split('T')[0] || hojeBrasilia(),
        descricaoOrcamento: orcamento.descricaoOrcamento || orcamento.DescricaoOrcamento || '',
        validadeOrcamento: (orcamento.validadeOrcamento || orcamento.ValidadeOrcamento || '').split('T')[0] || '',
        prazo_garantia: orcamento.prazo_garantia || orcamento.Prazo_garantia || orcamento.prazoGarantia || 12,
        desconto: parseFloat(orcamento.desconto || orcamento.Desconto || 0),
        valor: (parseFloat(orcamento.valor || orcamento.Valor || 0) + parseFloat(orcamento.lucro || orcamento.Lucro || 0)),
        custoOrcado: parseFloat(orcamento.custoOrcado || orcamento.CustoOrcado || 0)
      };
      
      // Lucro e desconto reabrem como foram informados (o lucro é sempre % sobre o custo, nunca composto)
      const percentualLucroDb = orcamento.percentualLucro ?? orcamento.PercentualLucro;
      if (percentualLucroDb !== null && percentualLucroDb !== undefined) {
        setAdicionalLucro(parseFloat(percentualLucroDb) || 0);
      } else {
        // Orçamentos salvos antes de guardar o %: Lucro ÷ custo (sem custo salvo, usa o Valor)
        const lucroDb = parseFloat(orcamento.lucro || orcamento.Lucro || 0);
        const custoDb = parseFloat(orcamento.custoOrcado || orcamento.CustoOrcado || 0);
        const baseLucro = custoDb > 0 ? custoDb : parseFloat(orcamento.valor || orcamento.Valor || 0);
        setAdicionalLucro(baseLucro > 0 ? Math.round((lucroDb / baseLucro) * 10000) / 100 : 0);
      }

      const tipoDescontoDb = orcamento.tipoDesconto || orcamento.TipoDesconto;
      const percentualDescontoDb = orcamento.percentualDesconto ?? orcamento.PercentualDesconto;
      if (tipoDescontoDb === 'percentual' && percentualDescontoDb !== null && percentualDescontoDb !== undefined) {
        setTipoDesconto('percentual');
        setDescontoPercentual(String(parseFloat(percentualDescontoDb) || 0));
      } else {
        // Desconto em R$: o valor já vem em formData.desconto
        setTipoDesconto('valor');
        setDescontoPercentual('');
      }

      console.log('📝 Dados do formulário preparados:', formData);
      setForm(formData);
      
      // Atualizar serviços
      if (servicosCarregados && servicosCarregados.length > 0) {
        const servicosFormatados = servicosCarregados.map(servico => ({
          chave: novaChaveServico(),
          responsaveis: [novoResponsavelServico()], // novos; os já salvos vêm de responsaveisExistentes
          id: servico.id || servico.Id,
          titulo: servico.titulo || servico.Titulo || servico.titulo || '',
          descricao: servico.descricao || servico.Descricao || servico.descricaoServico || servico.DescricaoServico || '',
          valorServico: parseFloat(servico.valorServico || servico.ValorServico || servico.valor || servico.Valor || 0),
          quantidade: parseInt(servico.quantidade || servico.Quantidade || 1)
        }));
        console.log('🔧 Serviços formatados e definidos:', servicosFormatados);
        setServicos(servicosFormatados);
      } else {
        console.log('⚠️ Nenhum serviço encontrado, iniciando com serviço vazio');
        setServicos([novoServicoOrcamento()]);
      }
      
      // Atualizar condições de pagamento
      if (condicoesCarregadas && condicoesCarregadas.length > 0) {
        const condicoesFormatadas = condicoesCarregadas.map(condicao => ({
          id: condicao.id || condicao.Id,
          descricao: condicao.descricao || condicao.Descricao || ''
        }));
        console.log('💳 Condições formatadas e definidas:', condicoesFormatadas);
        setCondicoesPagamento(condicoesFormatadas);
      } else {
        console.log('⚠️ Nenhuma condição encontrada, iniciando com condição vazia');
        setCondicoesPagamento([{ descricao: '' }]);
      }
      
      console.log('✅ Orçamento carregado completamente para edição!');

      // 4. Verificar se existe contrato vinculado ao orçamento
      try {
        const contratosResponse = await apiCall('Contrato');
        const contratos = Array.isArray(contratosResponse) ? contratosResponse : [];
        const contratoVinculado = contratos.find(c => 
          (c.orcamentosId || c.OrcamentosId) == orcamentoId
        );
        if (contratoVinculado) {
          console.log('🔒 Contrato encontrado para este orçamento — bloqueando edição');
          setTemContrato(true);
        } else {
          setTemContrato(false);
        }
      } catch (contratoError) {
        console.warn('⚠️ Erro ao verificar contrato do orçamento:', contratoError);
        setTemContrato(false);
      }

      // 5. Buscar responsáveis vinculados ao orçamento
      try {
        const responsaveisResponse = await apiCall(`Responsaveis/por-orcamento/${orcamentoId}`);
        console.log('📥 Responsáveis do orçamento:', responsaveisResponse);
        const respArr = Array.isArray(responsaveisResponse) ? responsaveisResponse : [];
        // Guarda as horas previstas originais para salvar só as que forem alteradas
        setResponsaveisExistentes(respArr.map(r => ({
          ...r,
          horasPrevistasOriginal: parseFloat(r.horasPrevistas ?? r.HorasPrevistas) || 0
        })));
        // Não preencher o formulário de criação de novos — apenas mostra os existentes
      } catch (respError) {
        console.warn('⚠️ Erro ao carregar responsáveis do orçamento:', respError);
        setResponsaveisExistentes([]);
      }

      // 5. Buscar serviços terceiros vinculados ao orçamento
      try {
        const stResponse = await apiCall(`ServicosTerceiros/orcamento/${orcamentoId}`);
        console.log('📥 Serviços Terceiros do orçamento:', stResponse);
        const stArr = Array.isArray(stResponse) ? stResponse : [];
        setServicosTerceirosExistentes(stArr);
      } catch (stError) {
        console.warn('⚠️ Erro ao carregar serviços terceiros do orçamento:', stError);
        setServicosTerceirosExistentes([]);
      }

      // Salvar snapshot original (sem status) para validação futura de aprovação
      setSnapshotOriginal({
        form: {
          oportunidadeId: formData.oportunidadeId || '',
          id_cliente: formData.id_cliente || '',
          dataCriacao: formData.dataCriacao || '',
          descricaoOrcamento: formData.descricaoOrcamento || '',
          validadeOrcamento: formData.validadeOrcamento || '',
          prazo_garantia: formData.prazo_garantia || 12,
          desconto: formData.desconto || 0,
          valor: formData.valor || 0
        },
        servicos: (servicosCarregados || []).map(s => ({
          titulo: s.titulo || s.Titulo || '',
          descricao: s.descricao || s.Descricao || '',
          valorServico: parseFloat(s.valorServico || s.ValorServico || s.valor || s.Valor || 0),
          quantidade: parseInt(s.quantidade || s.Quantidade || 1)
        })),
        condicoes: (condicoesCarregadas || []).map(c => ({
          descricao: c.descricao || c.Descricao || ''
        }))
      });
      
    } catch (error) {
      console.error('❌ Erro ao carregar orçamento:', error);
      setError('Erro ao carregar dados do orçamento. Alguns campos podem estar vazios.');
      
      // Em caso de erro, ainda tenta preencher com os dados básicos que temos
      if (orcamentoParaEditar) {
        console.log('🔄 Usando dados básicos do card como fallback');
        setForm({
          oportunidadeId: orcamentoParaEditar.oportunidadeId || orcamentoParaEditar.OportunidadeId || '',
          id_cliente: orcamentoParaEditar.id_cliente || orcamentoParaEditar.Id_cliente || orcamentoParaEditar.clienteId || orcamentoParaEditar.ClienteId || '',
          status: orcamentoParaEditar.status || orcamentoParaEditar.Status || 'Rascunho',
          dataCriacao: (orcamentoParaEditar.dataCriacao || orcamentoParaEditar.DataCriacao || '').split('T')[0] || hojeBrasilia(),
          descricaoOrcamento: orcamentoParaEditar.descricaoOrcamento || orcamentoParaEditar.DescricaoOrcamento || '',
          validadeOrcamento: (orcamentoParaEditar.validadeOrcamento || orcamentoParaEditar.ValidadeOrcamento || '').split('T')[0] || '',
          prazo_garantia: orcamentoParaEditar.prazo_garantia || orcamentoParaEditar.Prazo_garantia || 12,
          desconto: orcamentoParaEditar.desconto || orcamentoParaEditar.Desconto || 0,
          valor: orcamentoParaEditar.valor || orcamentoParaEditar.Valor || 0,
          custoOrcado: orcamentoParaEditar.custoOrcado || orcamentoParaEditar.CustoOrcado || 0
        });
        setServicos([novoServicoOrcamento()]);
        setCondicoesPagamento([{ descricao: '' }]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Funções auxiliares para obter nomes legíveis
  const obterNomeCliente = (clienteId) => {
    // Prioridade: clienteSelecionado (popup) > cliente do orçamento em edição > busca na lista
    if (clienteSelecionado) {
      return clienteSelecionado.nome || clienteSelecionado.Nome || `Cliente ${clienteSelecionado.id || clienteSelecionado.Id}`;
    }
    
    // Se estamos editando um orçamento, buscar dados do cliente
    if (orcamentoParaEditar && !clienteId) {
      clienteId = orcamentoParaEditar.id_cliente || orcamentoParaEditar.Id_cliente || orcamentoParaEditar.clienteId || orcamentoParaEditar.ClienteId;
    }
    
    const cliente = clientes.find(c => (c.id || c.Id) == clienteId);
    if (cliente) {
      return cliente.nome || cliente.Nome || `Cliente ${cliente.id || cliente.Id}`;
    }
    
    return clienteId ? `Cliente ${clienteId}` : '';
  };

  const obterTituloOportunidade = (oportunidadeId) => {
    // Prioridade: oportunidadeSelecionada (popup) > oportunidade do orçamento em edição > busca na lista
    if (oportunidadeSelecionada) {
      return oportunidadeSelecionada.titulo || oportunidadeSelecionada.Titulo || 
             oportunidadeSelecionada.descricao || oportunidadeSelecionada.Descricao || 
             `Oportunidade ${oportunidadeSelecionada.id || oportunidadeSelecionada.Id}`;
    }
    
    // Se estamos editando um orçamento, buscar dados da oportunidade
    if (orcamentoParaEditar && !oportunidadeId) {
      oportunidadeId = orcamentoParaEditar.oportunidadeId || orcamentoParaEditar.OportunidadeId;
    }
    
    const oportunidade = oportunidades.find(op => (op.id || op.Id) == oportunidadeId);
    if (oportunidade) {
      return oportunidade.titulo || oportunidade.Titulo || oportunidade.descricao || oportunidade.Descricao || `Oportunidade ${oportunidade.id || oportunidade.Id}`;
    }
    
    return oportunidadeId ? `Oportunidade ${oportunidadeId}` : '';
  };

  // Manipulação do formulário principal
  const handleFormChange = (field, value) => {
    setForm(prev => {
      const newForm = { ...prev, [field]: value };
      
      // Se o cliente foi alterado, limpar a oportunidade selecionada
      if (field === 'id_cliente') {
        newForm.oportunidadeId = '';
      }
      
      return newForm;
    });
  };

  // Manipulação dos serviços
  const handleServicoChange = (index, field, value) => {
    const novosServicos = [...servicos];
    novosServicos[index][field] = value;
    setServicos(novosServicos);
  };

  const adicionarServico = () => {
    setServicos([...servicos, novoServicoOrcamento()]);
  };

  const removerServico = (index) => {
    if (servicos.length <= 1) return;
    const servico = servicos[index];
    // Serviço já salvo: ao salvar o orçamento, os responsáveis e matérias-primas dele são excluídos junto
    if (servico.id &&
        !window.confirm('Os responsáveis e matérias-primas deste serviço serão excluídos junto com ele. Remover o serviço?')) {
      return;
    }
    delete refsMateriaPrimaServico.current[servico.chave];
    setValorMateriaPrimaPorServico(prev => {
      const { [servico.chave]: _removido, ...resto } = prev;
      return resto;
    });
    setServicos(servicos.filter((_, i) => i !== index));
  };

  // Responsáveis novos dentro de um serviço
  const alterarResponsavelServico = (indexServico, indexResp, campo, valor) => {
    setServicos(prev => prev.map((s, i) => i !== indexServico ? s : {
      ...s,
      responsaveis: s.responsaveis.map((r, j) => (j === indexResp ? { ...r, [campo]: valor } : r))
    }));
  };
  const adicionarResponsavelServico = (indexServico) => {
    setServicos(prev => prev.map((s, i) => i !== indexServico ? s : { ...s, responsaveis: [...s.responsaveis, novoResponsavelServico()] }));
  };
  const removerResponsavelServico = (indexServico, indexResp) => {
    // O principal acompanha a linha (se a removida era a principal, fica sem principal)
    const chaveServico = servicos[indexServico]?.chave;
    setPrincipalNovo(atual => {
      if (!atual || !atual.startsWith(`${chaveServico}:`)) return atual;
      const indice = parseInt(atual.split(':').pop());
      if (indice === indexResp) return null;
      return indice > indexResp ? `${chaveServico}:${indice - 1}` : atual;
    });
    setServicos(prev => prev.map((s, i) => i !== indexServico ? s : { ...s, responsaveis: s.responsaveis.filter((_, j) => j !== indexResp) }));
  };

  // Manipulação das condições de pagamento
  const handleCondicaoChange = (index, value) => {
    const novasCondicoes = [...condicoesPagamento];
    novasCondicoes[index].descricao = value;
    setCondicoesPagamento(novasCondicoes);
  };

  const adicionarCondicao = () => {
    setCondicoesPagamento([...condicoesPagamento, { descricao: '' }]);
  };

  const removerCondicao = (index) => {
    if (condicoesPagamento.length > 1) {
      setCondicoesPagamento(condicoesPagamento.filter((_, i) => i !== index));
    }
  };

  // uploadPDF removido - aprovação agora é via link de aceite

  // Validação do formulário
  const validarFormulario = () => {
    if (!form.oportunidadeId) {
      setError('Selecione uma oportunidade');
      return false;
    }
    if (!form.id_cliente) {
      setError('Selecione um cliente');
      return false;
    }
    if (!form.validadeOrcamento) {
      setError('Informe a validade do orçamento');
      return false;
    }
    if (servicos.some(s => !s.titulo || !s.descricao)) {
      setError('Todos os serviços devem ter título e descrição');
      return false;
    }
    // Todo serviço precisa de pelo menos um responsável (novo selecionado ou já salvo)
    const indexSemResponsavel = servicos.findIndex(s =>
      !s.responsaveis.some(r => r.pessoaId) && responsaveisExistentesDoServico(s).length === 0
    );
    if (indexSemResponsavel >= 0) {
      setError(`O Serviço ${indexSemResponsavel + 1} precisa ter pelo menos um responsável`);
      return false;
    }
    if (condicoesPagamento.some(c => !c.descricao.trim())) {
      setError('Todas as condições de pagamento devem ter descrição');
      return false;
    }
    return true;
  };

  // POST dos responsáveis e matérias-primas novos de cada serviço.
  // idsServicos[i] = Id no banco do servicos[i] (mesma ordem da tela).
  const salvarItensDosServicos = async (orcamentoId, idsServicos) => {
    for (let i = 0; i < servicos.length; i++) {
      const servico = servicos[i];
      const servicoOrcamentoId = idsServicos[i];
      if (!servicoOrcamentoId) {
        console.warn(`Serviço "${servico.titulo}" sem Id após salvar: responsáveis e matérias-primas dele não foram vinculados.`);
        continue;
      }

      for (const resp of servico.responsaveis) {
        if (!resp.pessoaId) continue;
        try {
          await apiCall('Responsaveis', 'POST', {
            id: 0,
            orcamentoId: parseInt(orcamentoId),
            servicoOrcamentoId: parseInt(servicoOrcamentoId),
            pessoasId: parseInt(resp.pessoaId),
            horasPrevistas: parseFloat(resp.horasPrevistas) || 0,
            horasReais: parseFloat(resp.horasReais) || 0,
            // Principal escolhido (com um só no orçamento, o servidor já marca como principal)
            responsavelPrincipal: principalNovo === `${servico.chave}:${servico.responsaveis.indexOf(resp)}`
          });
        } catch (errResp) {
          console.error('Erro ao vincular responsável ao serviço do orçamento:', errResp);
        }
      }

      const refMateriaPrima = refsMateriaPrimaServico.current[servico.chave];
      if (refMateriaPrima) {
        try {
          await refMateriaPrima.alocarMateriaisParaTarefa(orcamentoId, servicoOrcamentoId);
        } catch (errMat) {
          console.warn('Aviso: erro ao alocar matérias-primas do serviço (orçamento foi salvo):', errMat);
          alert('O orçamento foi salvo, mas houve erro nas matérias-primas: ' + (errMat.message || errMat));
        }
      }
    }
  };

  // Ids dos serviços depois da edição: os já salvos mantêm o Id; os novos recebem Ids
  // crescentes na ordem em que foram enviados (o PUT devolve o PDF, não a lista)
  const obterIdsServicosAposEdicao = async (orcamentoId) => {
    const idsConhecidos = servicos.map(s => s.id).filter(Boolean);
    const resposta = await apiCall(`ServicosOrcamento/orcamento/${orcamentoId}`);
    const lista = Array.isArray(resposta) ? resposta : (resposta?.servicos || resposta?.Servicos || []);
    const idsNovos = lista
      .map(s => s.id ?? s.Id)
      .filter(id => id && !idsConhecidos.includes(id))
      .sort((a, b) => a - b);
    let proximo = 0;
    return servicos.map(s => s.id || idsNovos[proximo++] || null);
  };

  // PUT das horas previstas alteradas nos responsáveis já vinculados (edição)
  const atualizarHorasResponsaveisExistentes = async (orcamentoId) => {
    const alterados = responsaveisExistentes.filter(r =>
      (parseFloat(r.horasPrevistas) || 0) !== (r.horasPrevistasOriginal || 0)
    );

    for (const resp of alterados) {
      const respId = resp.id || resp.responsaveisId || resp.ResponsaveisId;
      if (!respId) continue;

      try {
        // O PUT sobrescreve todos os campos: busca o registro completo para não perder outros vínculos
        const atual = await apiCall(`Responsaveis/${respId}`);
        await apiCall(`Responsaveis/${respId}`, 'PUT', {
          id: respId,
          pessoasId: atual.pessoasId,
          perfilResponsavelId: atual.perfilResponsavelId,
          tarefasId: atual.tarefasId,
          id_cliente: atual.id_cliente,
          producaoId: atual.producaoId,
          entregaId: atual.entregaId,
          montagemId: atual.montagemId,
          manutencaoId: atual.manutencaoId,
          garantiaId: atual.garantiaId,
          orcamentoId: parseInt(orcamentoId),
          horasPrevistas: parseFloat(resp.horasPrevistas) || 0,
          horasReais: atual.horasReais
        });
      } catch (errResp) {
        console.error('Erro ao atualizar horas previstas do responsável:', errResp);
      }
    }
  };

  // POST de serviços terceiros vinculados ao orçamento
  const postServicosTerceirosParaOrcamento = async (orcamentoId) => {
    for (let i = 0; i < servicosTerceirosOrc.length; i++) {
      const st = servicosTerceirosOrc[i];
      if (!st.descricao || !st.descricao.trim()) continue;

      try {
        const payloadST = {
          id: 0,
          descricao: st.descricao.trim(),
          valor: parseFloat(st.valor) || 0,
          orcamentoId: parseInt(orcamentoId),
          status: st.status || 'Pendente',
          fornecedorId: st.fornecedorId ? parseInt(st.fornecedorId, 10) : null,
          tipoServicoId: st.tipoServicoId ? parseInt(st.tipoServicoId, 10) : null,
          dataEntrega: st.dataExecucao || null,
          id_cliente: form.id_cliente ? parseInt(form.id_cliente, 10) : null
        };
        console.log('Criando serviço terceiro vinculado ao orçamento:', payloadST);
        await apiCall('ServicosTerceiros', 'POST', payloadST);
      } catch (errST) {
        console.error('Erro ao vincular serviço terceiro ao orçamento:', errST);
      }
    }
  };

  // Submissão do formulário
  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    // Diminuição de matéria-prima sem dizer o destino do material (perdido / volta ao estoque): não salva
    const refsMateriaPrima = Object.values(refsMateriaPrimaServico.current || {}).filter(Boolean);
    for (const refMp of refsMateriaPrima) {
      if (refMp.validarAlteracoes && !refMp.validarAlteracoes()) return;
    }

    // Validação de aprovação: se status -> Aprovado e é edição
    if (orcamentoParaEditar && form.status === 'Aprovado' && snapshotOriginal) {
      const mudouForm = (
        (snapshotOriginal.form.oportunidadeId || '') !== (form.oportunidadeId || '') ||
        (snapshotOriginal.form.id_cliente || '') !== (form.id_cliente || '') ||
        (snapshotOriginal.form.dataCriacao || '') !== (form.dataCriacao || '') ||
        (snapshotOriginal.form.descricaoOrcamento || '') !== (form.descricaoOrcamento || '') ||
        (snapshotOriginal.form.validadeOrcamento || '') !== (form.validadeOrcamento || '') ||
        parseInt(snapshotOriginal.form.prazo_garantia || 0) !== parseInt(form.prazo_garantia || 0) ||
        parseFloat(snapshotOriginal.form.desconto || 0) !== parseFloat(form.desconto || 0) ||
        parseFloat(snapshotOriginal.form.valor || 0) !== parseFloat(form.valor || 0) ||
        parseFloat(snapshotOriginal.form.custoOrcado || 0) !== parseFloat(form.custoOrcado || 0)
      );

      const normalizarServ = arr => arr.map(s => ({
        titulo: (s.titulo || '').trim(),
        descricao: (s.descricao || '').trim(),
        valorServico: parseFloat(s.valorServico || 0) || 0,
        quantidade: parseInt(s.quantidade || 1) || 1
      }));

      const origServ = normalizarServ(snapshotOriginal.servicos || []);
      const atualServ = normalizarServ(servicos || []);

      const mudouServicos = (
        origServ.length !== atualServ.length ||
        origServ.some((s, i) => !atualServ[i] ||
          s.titulo !== atualServ[i].titulo ||
          s.descricao !== atualServ[i].descricao ||
          s.valorServico !== atualServ[i].valorServico ||
          s.quantidade !== atualServ[i].quantidade)
      );

      const normalizarCond = arr => arr.map(c => (c.descricao || '').trim());
      const origCond = normalizarCond(snapshotOriginal.condicoes || []);
      const atualCond = normalizarCond(condicoesPagamento || []);
      const mudouCondicoes = (
        origCond.length !== atualCond.length ||
        origCond.some((c, i) => c !== atualCond[i])
      );

      if (mudouForm || mudouServicos || mudouCondicoes) {
        const msg = 'Houve edições no orçamento (campos, serviços ou condições). Não é possível aprovar.';
        console.error(msg);
        setError(msg);
        return; // Bloqueia submissão
      }
    }

    if (!validarFormulario()) {
      return;
    }

    // Declarar payload no escopo da função para ser acessível no catch
    let payload;
    // Flag para notificar o pai APENAS depois do loading terminar
    let shouldNotify = false;
    let notifyResultado = null;
    let notifyOptions = null;

    try {
      setLoading(true);

      // Custo (responsáveis + matérias-primas + serviços terceiros) e lucro em R$ sobre o custo
      const custoOrcamento = parseFloat(valorBaseOrcamento) || 0;
      const lucroOrcamento = Math.round(custoOrcamento * (parseFloat(adicionalLucro) || 0)) / 100;
      // Guarda como lucro e desconto foram informados, para a edição reabrir com os mesmos valores
      const configuracaoLucroDesconto = {
        PercentualLucro: parseFloat(adicionalLucro) || 0,
        TipoDesconto: tipoDesconto,
        PercentualDesconto: tipoDesconto === 'percentual' ? (parseFloat(descontoPercentual) || 0) : null
      };

      // Prepara o payload conforme esperado pelo backend (nomes corretos dos campos)
      const orcamentoData = {
        Id: orcamentoParaEditar ? orcamentoParaEditar.id : undefined,
        OportunidadeId: form.oportunidadeId,
        Id_cliente: form.id_cliente,
        Status: form.status || 'Rascunho',
        DataCriacao: form.dataCriacao || hojeBrasilia(),
        DescricaoOrcamento: form.descricaoOrcamento || '',
        ValidadeOrcamento: form.validadeOrcamento ? new Date(form.validadeOrcamento).toISOString() : null,
        Prazo_garantia: parseInt(form.prazo_garantia) || 12,
        Desconto: descontoReais, // sempre em R$ (mesmo quando informado em %)
        Valor: custoOrcamento,
        preco_final: parseFloat(form.valor) || 0,
        CustoOrcado: custoOrcamento,
        Lucro: lucroOrcamento,
        ...configuracaoLucroDesconto
        // Removido completamente arquivoPDFOrcamento e ArquivoPDFOrcamento
      };

      // Payload na estrutura OrcamentoCompleto esperada pelo backend
      payload = {
        orcamento: orcamentoData,
        servicos: servicos.map(servico => ({
          Id: servico.id || undefined,
          IdOrcamento: orcamentoParaEditar ? orcamentoParaEditar.id : undefined,
          Titulo: servico.titulo || servico.Titulo,
          Status: servico.status || servico.Status || 'Ativo',
          Descricao: servico.descricao || servico.Descricao,
          ValorServico: custoDoServico(servico), // custo do serviço (responsáveis + matérias-primas)
          Quantidade: parseInt(servico.quantidade || servico.Quantidade) || 1
        })),
        condicoes: condicoesPagamento.map(condicao => ({
          Id: condicao.id || undefined,
          OrcamentoId: orcamentoParaEditar ? orcamentoParaEditar.id : undefined,
          Descricao: condicao.descricao || condicao.Descricao
        }))
      };

      console.log('📦 Payload a ser enviado:', JSON.stringify(payload, null, 2));
      console.log('🔍 Campos no orcamento:', Object.keys(payload.orcamento));
      console.log('🔍 Verificando campos PDF removidos:', {
        arquivoPDFOrcamento: payload.orcamento.arquivoPDFOrcamento,
        ArquivoPDFOrcamento: payload.orcamento.ArquivoPDFOrcamento
      });

      let resultado;
      let uploadedPdfSuccess = false; // Flag to indicate if a PDF was uploaded automatically
  if (orcamentoParaEditar) {
        // 🔄 EDIÇÃO USANDO ENDPOINT COMPLETO DO BACKEND
        console.log(`✏️ Editando orçamento ID: ${orcamentoParaEditar.id}`);
        
        // ========== PREPARAR PAYLOAD COMPLETO ==========
        const orcamentoData = {
          Id: orcamentoParaEditar.id,
          OportunidadeId: parseInt(form.oportunidadeId) || null,
          Id_cliente: parseInt(form.id_cliente) || null,
          Status: form.status,
          DataCriacao: form.dataCriacao,
          DescricaoOrcamento: form.descricaoOrcamento,
          ValidadeOrcamento: form.validadeOrcamento,
          Desconto: descontoReais, // sempre em R$ (mesmo quando informado em %)
          Valor: custoOrcamento,
          preco_final: parseFloat(form.valor) || 0,
          CustoOrcado: custoOrcamento,
          Lucro: lucroOrcamento,
          ...configuracaoLucroDesconto,
          ArquivoPDFOrcamento: null // Não incluir PDF na edição normal
        };

        const servicosData = servicos.map(servico => ({
          Id: servico.id || undefined,
          IdOrcamento: orcamentoParaEditar.id,
          Titulo: servico.titulo || servico.Titulo,
          Status: servico.status || servico.Status || 'Ativo',
          Descricao: servico.descricao || servico.Descricao,
          ValorServico: custoDoServico(servico), // custo do serviço (responsáveis + matérias-primas)
          Quantidade: parseInt(servico.quantidade || servico.Quantidade) || 1
        }));

        const condicoesData = condicoesPagamento.map(condicao => ({
          Id: condicao.id || undefined,
          OrcamentoId: orcamentoParaEditar.id,
          Descricao: condicao.descricao || condicao.Descricao
        }));

        const payloadCompleto = {
          Orcamento: orcamentoData,
          Servicos: servicosData,
          Condicoes: condicoesData
        };

        console.log('📋 Payload completo sendo enviado:', JSON.stringify(payloadCompleto, null, 2));
        
        try {
          resultado = await apiCall(`Orcamento/${orcamentoParaEditar.id}`, 'PUT', payloadCompleto);
          console.log('✅ Orçamento editado com sucesso!');
        } catch (editError) {
          console.error('❌ Erro durante a edição:', editError.message);
          throw new Error(`Erro ao atualizar orçamento: ${editError.message}`);
        }

        // Responsáveis e matérias-primas novos de cada serviço + horas previstas alteradas dos existentes
        try {
          const idsServicos = await obterIdsServicosAposEdicao(orcamentoParaEditar.id);
          await salvarItensDosServicos(orcamentoParaEditar.id, idsServicos);
        } catch (errServicos) {
          console.error('Erro ao vincular itens aos serviços (orçamento foi atualizado):', errServicos);
        }
        await atualizarHorasResponsaveisExistentes(orcamentoParaEditar.id);

        // POST de serviços terceiros (edição)
        await postServicosTerceirosParaOrcamento(orcamentoParaEditar.id);
        
        // Verificar se a operação foi bem-sucedida
  if (resultado && (resultado.success || resultado.id || resultado.Id || resultado.status >= 200)) {
          console.log('Orçamento atualizado com sucesso');
          
          // Mostrar mensagem de sucesso
          setMostrarSucesso(true);
          setTimeout(() => setMostrarSucesso(false), 2500);
          
          // Chama callback de sucesso se fornecido
          if (onSalvarSucesso) {
            shouldNotify = true;
            notifyResultado = resultado;
            notifyOptions = { uploadedPdf: uploadedPdfSuccess };
          }
        } else {
          throw new Error('Resposta inesperada do servidor na edição');
        }
      } else {
        // Criação - usar POST com OrcamentoCompleto
        console.log('Criando novo orçamento');
        resultado = await apiCall('Orcamento', 'POST', payload);
        console.log('Resultado POST:', resultado);

        // Extrair o ID do orçamento recém-criado
        const novoOrcamentoId = resultado?.id || resultado?.Id || resultado?.orcamentoId || resultado?.OrcamentoId;

        if (novoOrcamentoId) {
          // Responsáveis e matérias-primas de cada serviço (o POST devolve os Ids na ordem enviada)
          const idsServicos = (resultado?.servicos || resultado?.Servicos || []).map(s => s.id ?? s.Id);
          await salvarItensDosServicos(novoOrcamentoId, idsServicos);

          // POST de serviços terceiros
          await postServicosTerceirosParaOrcamento(novoOrcamentoId);
        }
        
        // Verificar se a operação foi bem-sucedida (com PDF automático)
        if (resultado && (resultado.success || resultado.id || resultado.Id || resultado.orcamentoId || resultado.message || resultado.status >= 200)) {
          console.log('Orçamento criado com sucesso');
          console.log('Detalhes da resposta:', resultado);
          
          // Mostrar mensagem de sucesso
          setMostrarSucesso(true);
          setTimeout(() => setMostrarSucesso(false), 2500);
          
          // Chama callback de sucesso se fornecido
          if (onSalvarSucesso) {
            shouldNotify = true;
            notifyResultado = resultado;
            notifyOptions = { uploadedPdf: false };
          }
          
          // Reset do formulário após criação bem-sucedida
          setForm({
            oportunidadeId: '',
            id_cliente: '',
            status: 'Rascunho',
            dataCriacao: hojeBrasilia(),
            descricaoOrcamento: '',
            validadeOrcamento: '',
            prazo_garantia: 12,
            desconto: '',
            valor: 0
          });
          setServicos([novoServicoOrcamento()]);
          setCondicoesPagamento([{ descricao: '' }]);
          setValorMateriaPrimaPorServico({});
          setAdicionalLucro('');
          setTipoDesconto('valor');
          setDescontoPercentual('');
        } else {
          throw new Error('Resposta inesperada do servidor na criação');
        }
      }

    } catch (error) {
      console.error('Erro ao salvar orçamento:', error);
      
      // Desabilitar botão por 5 segundos
      setBotaoDesabilitado(true);
      setTimeout(() => setBotaoDesabilitado(false), 5000);
      
      // Extrair detalhes específicos do erro para ajudar no debug
      let mensagemErro = 'Erro ao salvar orçamento';
      
      if (error.message) {
        console.error('Mensagem do erro:', error.message);
        
        // Se é erro de validação (400), tentar extrair detalhes
        if (error.message.includes('400') || error.message.includes('validation')) {
          mensagemErro = 'Erro de validação: ' + error.message;
          
          // Log adicional para debug
          console.error('Erro de validação detectado. Payload enviado foi:', JSON.stringify(payload, null, 2));
        }
        // Verificar se é realmente um erro ou apenas problema de conectividade
        else if (error.message.includes('Nenhuma URL da API funcionou')) {
          // Verificar se alguma operação pode ter sido bem-sucedida
          console.log('Verificando se orçamento foi salvo apesar do erro de API...');
          
          // Se chegamos aqui após validação bem-sucedida, o backend pode ter funcionado
          // mas com problemas na resposta. Vamos dar uma segunda chance.
          setTimeout(() => {
            if (window.confirm('Houve um problema de conectividade, mas o orçamento pode ter sido salvo. Deseja fechar o formulário?')) {
                  if (onSalvarSucesso) {
                        shouldNotify = true;
                        notifyResultado = null;
                        notifyOptions = { uploadedPdf: false };
                      }
            }
          }, 1000);
          
          mensagemErro = 'Problema de conectividade detectado. Verifique se o orçamento foi salvo.';
        } else {
          mensagemErro = error.message;
        }
      }
      
      setError(mensagemErro);
    } finally {
      setLoading(false);
      // Only show the popup / notify after loading has ended so the LoadingOverlay doesn't obscure it
      if (shouldNotify && onSalvarSucesso) {
        try { onSalvarSucesso(notifyResultado, notifyOptions); } catch (e) { console.error('Failed calling onSalvarSucesso after loading end', e); }
      }
    }
  };

  const formatarReais = (v) => (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const estiloRotulo = { fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 };
  const estiloBotaoMais = {
    padding: '10px 14px', backgroundColor: 'rgb(235, 133, 0)', color: 'white', border: 'none',
    borderRadius: 6, cursor: 'pointer', fontSize: 18, fontWeight: 700, lineHeight: 1, minWidth: 40, height: 42
  };
  const estiloBotaoRemover = { ...estiloBotaoMais, backgroundColor: '#f44336' };
  // Containers brancos de responsáveis e matérias-primas dentro de cada serviço
  const estiloContainerServico = { marginTop: 14, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 14 };
  const estiloTituloContainerServico = { fontSize: 13, fontWeight: 700, color: '#555', display: 'block', marginBottom: 10 };
  const estiloTotalContainerServico = {
    marginTop: 12, paddingTop: 10, borderTop: '1px solid #eee',
    textAlign: 'right', fontSize: 14, fontWeight: 700, color: '#2e7d32'
  };

  // Responsáveis do orçamento todo (salvos + novos com pessoa): o principal só aparece com mais de um
  const totalResponsaveisOrcamento = () =>
    responsaveisExistentes.length + servicos.reduce((t, s) => t + s.responsaveis.filter(r => r.pessoaId).length, 0);

  // Marca/desmarca um responsável já salvo como principal do orçamento (grava na hora; só um)
  const alternarPrincipalExistente = async (resp) => {
    const respId = idResponsavel(resp);
    const marcar = !ehPrincipal(resp);
    try {
      await salvarPrincipal(apiCall, respId, marcar);
      setResponsaveisExistentes(prev => prev.map(r => ({
        ...r,
        responsavelPrincipal: idResponsavel(r) === respId ? marcar : (marcar ? false : ehPrincipal(r))
      })));
      if (marcar) setPrincipalNovo(null);
    } catch (err) {
      alert('Erro ao definir o responsável principal: ' + err.message);
    }
  };

  // Responsável já salvo (edição): horas previstas editáveis e remoção
  const renderResponsavelExistente = (resp) => {
    const respId = idResponsavel(resp);
    return (
      <div key={respId} style={{
        padding: 12, background: '#fafafa', borderRadius: 8, border: '1px solid #e0e0e0',
        display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 10, alignItems: 'end'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={estiloRotulo}>Nome</label>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{resp.pessoaNome || resp.PessoaNome || resp.nome || resp.Nome || '—'}</div>
          {/* Responsável principal do orçamento: só um (com um só, ele já é o principal) */}
          {!isReadOnly && totalResponsaveisOrcamento() > 1 ? (
            <button
              type="button"
              onClick={() => alternarPrincipalExistente(resp)}
              title={ehPrincipal(resp) ? 'Clique para deixar de ser o principal' : 'Tornar este o responsável principal do orçamento (só um)'}
              style={{ marginTop: 6, alignSelf: 'flex-start', padding: '3px 10px', borderRadius: 12, border: '1px solid rgb(235, 133, 0)', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: ehPrincipal(resp) ? 'rgb(235, 133, 0)' : '#fff', color: ehPrincipal(resp) ? '#fff' : 'rgb(235, 133, 0)' }}
            >
              {ehPrincipal(resp) ? '⭐ Principal' : '☆ Tornar principal'}
            </button>
          ) : ehPrincipal(resp) && (
            <div style={{ marginTop: 4, fontSize: 11, fontWeight: 700, color: 'rgb(235, 133, 0)' }}>⭐ Principal</div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={estiloRotulo}>Hrs Previstas</label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={resp.horasPrevistas ?? ''}
            onChange={(e) => {
              const valor = e.target.value;
              setResponsaveisExistentes(prev => prev.map(r => (idResponsavel(r) === respId ? { ...r, horasPrevistas: valor } : r)));
            }}
            disabled={loading || isReadOnly}
            placeholder="0"
            title="Horas previstas (custo = custo/hora × horas previstas)"
            style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', width: 100, fontSize: 14 }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={estiloRotulo}>Custo/Hora</label>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1976d2' }}>
            {(resp.pessoaCustoHora || resp.PessoaCustoHora || resp.custoHora || resp.CustoHora) ? `R$ ${parseFloat(resp.pessoaCustoHora || resp.PessoaCustoHora || resp.custoHora || resp.CustoHora).toFixed(2)}/h` : '—'}
          </div>
        </div>
        <button
          type="button"
          disabled={isReadOnly}
          onClick={async () => {
            if (!respId) return;
            if (!window.confirm('Deseja remover este responsável do orçamento?')) return;
            try {
              await apiCall(`Responsaveis/${respId}`, 'DELETE');
              setResponsaveisExistentes(prev => prev.filter(r => idResponsavel(r) !== respId));
            } catch (err) {
              console.error('Erro ao remover responsável:', err);
              alert('Erro ao remover responsável: ' + err.message);
            }
          }}
          style={{ padding: '6px 12px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 700, height: 36 }}
          title="Remover responsável"
        >×</button>
      </div>
    );
  };

  // Responsáveis novos de um serviço: pessoa + horas previstas (Hrs Reais é preenchido depois da execução)
  // Pessoas para o select de responsáveis
  const mapearPessoas = (lista) => (Array.isArray(lista) ? lista : []).map(p => ({
    id: p.id || p.Id,
    nome: (p.dadosUsuario && p.dadosUsuario.nomeUsuario) ||
          (p.usuarioSistema && p.usuarioSistema.login) ||
          p.nome || p.Nome ||
          `Pessoa #${p.id || p.Id}`,
    custoHora: p.custoHora || p.CustoHora || 0
  }));

  // "Adicionar nova pessoa" num serviço: cadastra sem sair do orçamento, recarrega a lista e já coloca a pessoa no serviço
  const aoCriarPessoaNoServico = async (criada) => {
    try { setPessoas(mapearPessoas(await apiCall('Pessoas'))); } catch { /* lista fica como está */ }
    const id = criada?.id ?? criada?.Id;
    const indexServico = novaPessoaServico;
    if (!id || indexServico == null) return;
    setServicos(prev => prev.map((s, i) => {
      if (i !== indexServico) return s;
      const vazia = s.responsaveis.findIndex(r => !r.pessoaId);
      if (vazia >= 0) return { ...s, responsaveis: s.responsaveis.map((r, j) => (j === vazia ? { ...r, pessoaId: String(id) } : r)) };
      return { ...s, responsaveis: [...s.responsaveis, { ...novoResponsavelServico(), pessoaId: String(id) }] };
    }));
  };

  const renderResponsaveisNovos = (servico, indexServico) => (
    <>
      {servico.responsaveis.map((resp, indexResp) => (
        <div key={indexResp} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <select
            value={resp.pessoaId}
            onChange={(e) => alterarResponsavelServico(indexServico, indexResp, 'pessoaId', e.target.value)}
            disabled={loading || isReadOnly}
            style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', flex: '1 1 200px', minWidth: 200, fontSize: 14 }}
          >
            <option value="">Selecione uma pessoa</option>
            {pessoas.map((pessoa) => (
              <option key={pessoa.id} value={pessoa.id}>
                {pessoa.nome} {pessoa.custoHora ? `(R$ ${parseFloat(pessoa.custoHora).toFixed(2)}/h)` : ''}
              </option>
            ))}
          </select>

          <input
            type="number"
            step="0.5"
            min="0"
            value={resp.horasPrevistas}
            onChange={(e) => alterarResponsavelServico(indexServico, indexResp, 'horasPrevistas', e.target.value)}
            disabled={loading || isReadOnly}
            placeholder="Hrs Previstas"
            title="Horas previstas (custo = custo/hora × horas previstas)"
            style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', width: 130, height: 42, fontSize: 14 }}
          />

          {/* Com mais de um responsável no orçamento, pode marcar um como principal (opcional; só um) */}
          {resp.pessoaId && totalResponsaveisOrcamento() > 1 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: principalNovo === `${servico.chave}:${indexResp}` ? 'rgb(235, 133, 0)' : '#555', cursor: 'pointer' }}>
              <input
                type="checkbox"
                disabled={loading || isReadOnly}
                checked={principalNovo === `${servico.chave}:${indexResp}`}
                onChange={() => {
                  const chave = `${servico.chave}:${indexResp}`;
                  setPrincipalNovo(atual => (atual === chave ? null : chave));
                }}
              />
              Principal
            </label>
          )}

          <button type="button" onClick={() => adicionarResponsavelServico(indexServico)} disabled={loading || isReadOnly}
            style={estiloBotaoMais} title="Adicionar mais um responsável">+</button>
          <button type="button" onClick={() => removerResponsavelServico(indexServico, indexResp)} disabled={loading || isReadOnly}
            style={estiloBotaoRemover} title="Remover este responsável">×</button>
        </div>
      ))}
      {servico.responsaveis.length === 0 && (
        <button type="button" onClick={() => adicionarResponsavelServico(indexServico)} disabled={loading || isReadOnly}
          style={{ ...estiloBotaoMais, fontSize: 14 }}>+ Adicionar responsável</button>
      )}
      {!isReadOnly && (
        <button type="button" onClick={() => setNovaPessoaServico(indexServico)} disabled={loading}
          style={{ padding: '8px 14px', backgroundColor: '#fff', color: 'rgb(235, 133, 0)', border: '1px solid rgb(235, 133, 0)', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13, marginTop: 4 }}>
          Adicionar nova pessoa
        </button>
      )}
    </>
  );

  return (
    <div className="formulario-orcamento">
      <BotaoVoltarTopo />
      {novaPessoaServico != null && (
        <PopupNovaPessoa onFechar={() => setNovaPessoaServico(null)} onCriada={aoCriarPessoaNoServico} />
      )}
      <h2>{orcamentoParaEditar ? 'Editar Orçamento' : 'Novo Orçamento'}</h2>
      
      {/* Notificação de sucesso */}
      {mostrarSucesso && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'white',
          color: '#ff6600',
          padding: '15px 25px',
          borderRadius: '5px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          fontSize: '16px',
          fontWeight: '600',
          animation: 'fadeInOut 2.5s ease-in-out'
        }}>
          {orcamentoParaEditar ? 'Orçamento atualizado com sucesso!' : 'Orçamento salvo com sucesso!'}
        </div>
      )}
      
  {/* Mensagem de erro movida para baixo (acima dos botões). Removida desta posição. */}
      
      <form onSubmit={handleSubmit}>
        {temContrato && (
          <div style={{ background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: 6, padding: '12px 16px', marginBottom: 16, color: '#721c24', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            🔒 Este orçamento já possui um contrato gerado e não pode ser editado. Para fazer alterações, crie um novo orçamento.
          </div>
        )}
        {isReadOnly && !temContrato && (
          <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '10px 16px', marginBottom: 16, color: '#856404', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
            ⚠️ Este orçamento está aprovado. Todos os campos são somente leitura.
          </div>
        )}
        <div style={{ pointerEvents: isReadOnly ? 'none' : 'auto', opacity: isReadOnly ? 0.75 : 1 }}>
        {/* Dados Principais do Orçamento */}
        <div id="orc-secao-geral" className="secao-formulario">
          <h3>Dados do Orçamento</h3>
          
          {/* Primeira linha - duas colunas */}
          <div className="linha-campos">
            <div className="campo">
              <label>Cliente:</label>
              {(clienteSelecionado || orcamentoParaEditar) ? (
                // Mostra campo de texto com o nome do cliente quando ele foi selecionado nos popups ou estamos editando
                <div>
                  <input 
                    type="text"
                    value={obterNomeCliente(form.id_cliente)}
                    disabled
                    style={{
                      width: '200px',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '14px',
                      backgroundColor: '#f5f5f5',
                      cursor: 'not-allowed',
                      color: '#333',
                      fontWeight: 'bold'
                    }}
                  />
                  <small style={{color: '#28a745', fontSize: '12px', fontWeight: 'bold'}}>
                    {clienteSelecionado ? '✅ Cliente selecionado nos pop-ups anteriores' : '✅ Cliente do orçamento em edição'}
                  </small>
                </div>
              ) : (
                // Mostra dropdown normal quando não há cliente pré-selecionado
                <>
                  <select 
                    value={form.id_cliente} 
                    onChange={(e) => handleFormChange('id_cliente', e.target.value)}
                    required
                    disabled={loading}
                    style={{
                      width: '200px',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      fontSize: '14px',
                      backgroundColor: loading ? '#f5f5f5' : 'white',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">Selecione um cliente</option>
                    {clientes.map(cliente => (
                      <option key={cliente.id} value={cliente.id}>
                        {cliente.nome}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>

            <div className="campo">
              <label>Oportunidade:</label>
              {(oportunidadeSelecionada || orcamentoParaEditar) ? (
                // Mostra campo de texto com o título da oportunidade quando ela foi selecionada nos popups ou estamos editando
                <div>
                  <input 
                    type="text"
                    value={obterTituloOportunidade(form.oportunidadeId)}
                    disabled
                    style={{
                      width: '200px',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      fontSize: '14px',
                      backgroundColor: '#f5f5f5',
                      cursor: 'not-allowed',
                      color: '#333',
                      fontWeight: 'bold'
                    }}
                  />
                  <small style={{color: '#28a745', fontSize: '12px', fontWeight: 'bold'}}>
                    {oportunidadeSelecionada ? '✅ Oportunidade selecionada nos pop-ups anteriores' : '✅ Oportunidade do orçamento em edição'}
                  </small>
                </div>
              ) : (
                // Mostra dropdown normal quando não há oportunidade pré-selecionada
                <>
                  <select 
                    value={form.oportunidadeId} 
                    onChange={(e) => handleFormChange('oportunidadeId', e.target.value)}
                    required
                    disabled={loading}
                    style={{
                      width: '200px',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      fontSize: '14px',
                      backgroundColor: loading ? '#f5f5f5' : 'white',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">Selecione uma oportunidade</option>
                    {oportunidades
                      .filter(op => {
                        // Se não há cliente selecionado, mostrar todas as oportunidades
                        if (!form.id_cliente) return true;
                        // Filtrar por cliente selecionado - verificar ambos os campos possíveis
                        const opClienteId = op.clienteId || op.ClienteId || op.id_cliente;
                        return opClienteId === parseInt(form.id_cliente);
                      })
                      .map(oportunidade => (
                      <option key={oportunidade.id || oportunidade.Id} value={oportunidade.id || oportunidade.Id}>
                        {oportunidade.titulo || oportunidade.Oportunidade_Titulo || oportunidade.nome || oportunidade.descricao || `Oportunidade ${oportunidade.id || oportunidade.Id}`}
                      </option>
                    ))}
                  </select>
                  {form.id_cliente && oportunidades.filter(op => {
                    const opClienteId = op.clienteId || op.ClienteId || op.id_cliente;
                    return opClienteId === parseInt(form.id_cliente);
                  }).length === 0 && (
                    <small style={{color: '#666', fontSize: '12px'}}>
                      Nenhuma oportunidade encontrada para este cliente
                    </small>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Segunda linha - duas colunas */}
          <div className="linha-campos">
            <div className="campo">
              <label>Status:</label>
              <select 
                value={form.status} 
                onChange={(e) => handleFormChange('status', e.target.value)}
                disabled={loading}
                style={{
                  width: '200px',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '14px'
                }}
              >
                {isReadOnly && <option value="Aprovado">Aprovado</option>}
                {statusDisponiveis.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="campo">
              <label>Data de Criação:</label>
              <input 
                type="date" 
                value={form.dataCriacao} 
                readOnly
                title="Automática: a data em que o orçamento é criado"
                disabled
                style={{
                  width: '200px',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
          
          {/* Terceira linha - duas colunas */}
          <div className="linha-campos">
            <div className="campo">
              <label>Validade do Orçamento:</label>
              <input 
                type="date" 
                value={form.validadeOrcamento} 
                onChange={(e) => handleFormChange('validadeOrcamento', e.target.value)}
                required
                disabled={loading}
                style={{
                  width: '200px',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '14px'
                }}
              />
            </div>

            <div className="campo">
              <label>Prazo de Garantia (meses):</label>
              <input 
                type="number" 
                value={form.prazo_garantia} 
                onChange={(e) => handleFormChange('prazo_garantia', e.target.value)}
                min="1"
                disabled={loading}
                style={{
                  width: '200px',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Quarta linha - custo e valor total (calculados) */}
          <div className="linha-campos">
            <div className="campo">
              <label>Custo Orçado (R$):</label>
              <input
                type="number"
                step="0.01"
                value={form.custoOrcado}
                readOnly
                placeholder={"R$ 0,00"}
                title="Soma de responsáveis (custo/hora × horas previstas), matérias-primas e serviços terceiros. Custo interno - não aparece no PDF gerado"
                style={{
                  width: '180px',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '14px',
                  backgroundColor: '#fff9e6'
                }}
              />
            </div>

            <div className="campo">
              <label>Valor Total (R$):</label>
              <input 
                type="number" 
                step="0.01"
                value={form.valor} 
                readOnly
                placeholder={"R$ 0,00"}
                style={{
                  width: '180px',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '14px',
                  backgroundColor: '#f5f5f5'
                }}
              />
            </div>
          </div>

          {/* Descrição - linha única */}
          <div className="campo campo-completo">
            <label>Descrição do Orçamento:</label>
            <textarea 
              value={form.descricaoOrcamento} 
              onChange={(e) => handleFormChange('descricaoOrcamento', e.target.value)}
              rows="3"
              disabled={loading}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>
        </div>

        {/* Seção de Serviços */}
        <div id="orc-secao-servicos" className="secao-formulario">
          <h3>Serviços do Orçamento</h3>
          
          {servicos.map((servico, index) => (
            <div key={servico.chave} className="servico-item" style={{border: '1px solid #ddd', padding: '15px', marginBottom: '10px', borderRadius: '5px', background: '#f5f5f5'}}>
              <div className="servico-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: 12, flexWrap: 'wrap'}}>
                <h4 style={{ margin: 0 }}>Serviço {index + 1}</h4>
                <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: '#2e7d32' }} title="Responsáveis + matérias-primas deste serviço">
                  Custo do serviço: {formatarReais(custoDoServico(servico))}
                </span>
                {servicos.length > 1 && (
                  <button 
                    type="button" 
                    onClick={() => removerServico(index)}
                    style={{background: 'red', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px'}}
                    disabled={loading}
                  >
                    Remover
                  </button>
                )}
              </div>
              
              <div className="campo">
                <label>Título do Serviço:</label>
                <input 
                  type="text" 
                  value={servico.titulo} 
                  onChange={(e) => handleServicoChange(index, 'titulo', e.target.value)}
                  required
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div className="campo">
                <label>Descrição do Serviço:</label>
                <textarea 
                  value={servico.descricao} 
                  onChange={(e) => handleServicoChange(index, 'descricao', e.target.value)}
                  rows="2"
                  required
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '14px',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Responsáveis do serviço (obrigatório pelo menos um) */}
              <div style={estiloContainerServico}>
                <label style={estiloTituloContainerServico}>👷 Responsáveis do serviço *</label>
                {responsaveisExistentesDoServico(servico).length > 0 && (
                  <div style={{ display: 'grid', gap: 10, marginBottom: 10 }}>
                    {responsaveisExistentesDoServico(servico).map(renderResponsavelExistente)}
                  </div>
                )}
                {renderResponsaveisNovos(servico, index)}
                <div style={estiloTotalContainerServico}>
                  Total dos responsáveis: {formatarReais(custoResponsaveisDoServico(servico))}
                </div>
              </div>

              {/* Matérias-primas do serviço */}
              <div style={estiloContainerServico}>
                <label style={estiloTituloContainerServico}>🧱 Matérias-primas do serviço</label>
                <FormularioMateriaPrima
                  ref={(el) => { if (el) refsMateriaPrimaServico.current[servico.chave] = el; }}
                  tarefaId={orcamentoParaEditar && servico.id ? (orcamentoParaEditar.id || orcamentoParaEditar.Id) : null}
                  tipoId="orcamento"
                  somenteLeitura={isReadOnly}
                  servicoOrcamentoId={servico.id || null}
                  filtrarPorServico
                  modoEdicao={!!orcamentoParaEditar}
                  onValorTotalChange={(v) => setValorMateriaPrimaPorServico(prev => (prev[servico.chave] === v ? prev : { ...prev, [servico.chave]: v }))}
                />
                <div style={estiloTotalContainerServico}>
                  Total das matérias-primas: {formatarReais(custoMateriasPrimasDoServico(servico))}
                </div>
              </div>
            </div>
          ))}
          
          <button 
            type="button" 
            onClick={adicionarServico}
            style={{background: '#007bff', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '5px', marginBottom: '15px'}}
            disabled={loading}
          >
            Adicionar Serviço
          </button>
        </div>

        {/* Seção de Condições de Pagamento */}
        <div id="orc-secao-condicoes" className="secao-formulario">
          <h3>Condições de Pagamento</h3>
          
          {condicoesPagamento.map((condicao, index) => (
            <div key={index} className="condicao-item" style={{border: '1px solid #ddd', padding: '15px', marginBottom: '10px', borderRadius: '5px'}}>
              <div className="condicao-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                <h4>Condição {index + 1}</h4>
                {condicoesPagamento.length > 1 && (
                  <button 
                    type="button" 
                    onClick={() => removerCondicao(index)}
                    style={{background: 'red', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px'}}
                    disabled={loading}
                  >
                    Remover
                  </button>
                )}
              </div>
              
              <div className="campo">
                <label>Descrição da Condição:</label>
                <textarea 
                  value={condicao.descricao} 
                  onChange={(e) => handleCondicaoChange(index, e.target.value)}
                  rows="2"
                  required
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '14px',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            </div>
          ))}
          
          <button 
            type="button" 
            onClick={adicionarCondicao}
            style={{background: '#007bff', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '5px', marginBottom: '15px'}}
            disabled={loading}
          >
            Adicionar Condição
          </button>
        </div>

        {/* Seção de Serviços Terceiros do Orçamento */}
        <div id="orc-secao-servicos-terceiros" className="secao-formulario">
          <h3>Serviços Terceiros</h3>

          {/* Serviços terceiros já salvos (modo edição) */}
          {orcamentoParaEditar && servicosTerceirosExistentes.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 8 }}>Serviços Terceiros Vinculados:</label>
              <div style={{ display: 'grid', gap: 10 }}>
                {servicosTerceirosExistentes.map((st, idx) => (stEditando && stEditando.idx === idx) ? (
                  <div key={st.id || st.Id || idx} style={{ padding: 12, background: '#fff8ef', borderRadius: 8, border: '1px solid rgb(235, 133, 0)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 8 }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Fornecedor</label>
                        <select
                          value={stEditando.fornecedorId}
                          onChange={e => setStEditando(prev => ({ ...prev, fornecedorId: e.target.value }))}
                          style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
                        >
                          <option value="">Selecione um fornecedor</option>
                          {fornecedoresOrc.map(f => (
                            <option key={f.id || f.fornecedorId} value={f.id || f.fornecedorId}>
                              {f.nomeFornecedor || f.nome || f.razaoSocial || `Fornecedor ${f.id || f.fornecedorId}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Tipo de Serviço</label>
                        <select
                          value={stEditando.tipoServicoId}
                          onChange={e => setStEditando(prev => ({ ...prev, tipoServicoId: e.target.value }))}
                          style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
                        >
                          <option value="">Selecione um tipo</option>
                          {tiposServicoOrc.map(ts => (
                            <option key={ts.id} value={ts.id}>{ts.descricaoTpServico || `Tipo ${ts.id}`}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Valor (R$)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={stEditando.valor}
                          onChange={e => setStEditando(prev => ({ ...prev, valor: e.target.value }))}
                          style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Data Execução</label>
                        <input
                          type="date"
                          value={stEditando.dataEntrega}
                          onChange={e => setStEditando(prev => ({ ...prev, dataEntrega: e.target.value }))}
                          style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }}>
                        <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Descrição *</label>
                        <input
                          type="text"
                          value={stEditando.descricao}
                          onChange={e => setStEditando(prev => ({ ...prev, descricao: e.target.value }))}
                          style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn-cancelar-forte"
                        onClick={() => setStEditando(null)}
                        style={{ padding: '6px 14px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                      >Cancelar</button>
                      <button
                        type="button"
                        onClick={() => salvarEdicaoServicoTerceiro(st, idx)}
                        style={{ padding: '6px 14px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                      >Salvar</button>
                    </div>
                  </div>
                ) : (
                  <div key={st.id || st.Id || idx} style={{
                    padding: 12, background: '#fafafa', borderRadius: 8, border: '1px solid #e0e0e0',
                    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto auto', gap: 10, alignItems: 'end'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Descrição</label>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{st.descricao || st.Descricao || 'Sem descrição'}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Valor</label>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1976d2' }}>R$ {(Number(st.valor ?? st.Valor) || 0).toFixed(2)}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Status</label>
                      <div style={{ fontSize: 14 }}>{st.status || st.Status || '—'}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Fornecedor</label>
                      <div style={{ fontSize: 14 }}>
                        {(() => {
                          const fId = st.fornecedorId || st.FornecedorId;
                          if (!fId) return '—';
                          const f = fornecedoresOrc.find(x => (x.id || x.fornecedorId) == fId);
                          return f ? (f.nomeFornecedor || f.nome || f.razaoSocial || `#${fId}`) : `#${fId}`;
                        })()}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={isReadOnly}
                      onClick={() => setStEditando({
                        idx,
                        descricao: st.descricao ?? st.Descricao ?? '',
                        valor: String(st.valor ?? st.Valor ?? ''),
                        fornecedorId: String(st.fornecedorId ?? st.FornecedorId ?? ''),
                        tipoServicoId: String(st.tipoServicoId ?? st.TipoServicoId ?? ''),
                        dataEntrega: String(st.dataEntrega ?? st.DataEntrega ?? '').slice(0, 10)
                      })}
                      style={{ padding: '6px 10px', backgroundColor: 'rgb(235, 133, 0)', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, height: 36 }}
                      title="Editar serviço terceiro"
                    >✏️</button>
                    <button
                      type="button"
                      disabled={isReadOnly}
                      onClick={async () => {
                        try {
                          await apiCall(`ServicosTerceiros/${st.id || st.Id}`, 'PUT', { ...dadosServicoTerceiro(st), orcamentoId: null });
                          setServicosTerceirosExistentes(prev => prev.filter((_, i) => i !== idx));
                        } catch (e) {
                          console.error('Erro ao remover serviço terceiro:', e);
                        }
                      }}
                      style={{ padding: '6px 12px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 700, height: 36 }}
                      title="Remover serviço terceiro"
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Adicionar novos serviços terceiros */}
          <label style={{ fontSize: 13, fontWeight: 600, color: '#555', display: 'block', marginBottom: 8 }}>
            {orcamentoParaEditar ? 'Adicionar Novos Serviços Terceiros:' : 'Serviços Terceiros:'}
          </label>
          {servicosTerceirosOrc.map((st, index) => (
            <div key={index} style={{ padding: 12, background: '#fafafa', borderRadius: 8, border: '1px solid #e0e0e0', marginBottom: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Fornecedor</label>
                  <select
                    value={st.fornecedorId}
                    onChange={e => {
                      const arr = [...servicosTerceirosOrc];
                      arr[index] = { ...arr[index], fornecedorId: e.target.value };
                      setServicosTerceirosOrc(arr);
                    }}
                    disabled={isReadOnly}
                    style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
                  >
                    <option value="">Selecione um fornecedor</option>
                    {fornecedoresOrc.map(f => (
                      <option key={f.id || f.fornecedorId} value={f.id || f.fornecedorId}>
                        {f.nomeFornecedor || f.nome || f.razaoSocial || `Fornecedor ${f.id || f.fornecedorId}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Tipo de Serviço</label>
                  <select
                    value={st.tipoServicoId}
                    onChange={e => {
                      const arr = [...servicosTerceirosOrc];
                      arr[index] = { ...arr[index], tipoServicoId: e.target.value };
                      setServicosTerceirosOrc(arr);
                    }}
                    disabled={isReadOnly}
                    style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
                  >
                    <option value="">Selecione um tipo</option>
                    {tiposServicoOrc.map(ts => (
                      <option key={ts.id} value={ts.id}>
                        {ts.descricaoTpServico || `Tipo ${ts.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Valor (R$)</label>
                  <input
                    type="number"
                    value={st.valor}
                    onChange={e => {
                      const arr = [...servicosTerceirosOrc];
                      arr[index] = { ...arr[index], valor: e.target.value };
                      setServicosTerceirosOrc(arr);
                    }}
                    placeholder="0.00"
                    disabled={isReadOnly}
                    min="0"
                    step="0.01"
                    style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Data Execução</label>
                  <input
                    type="date"
                    value={st.dataExecucao}
                    onChange={e => {
                      const arr = [...servicosTerceirosOrc];
                      arr[index] = { ...arr[index], dataExecucao: e.target.value };
                      setServicosTerceirosOrc(arr);
                    }}
                    disabled={isReadOnly}
                    style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Status</label>
                  <input
                    type="text"
                    value="Pendente"
                    readOnly
                    disabled
                    style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, backgroundColor: '#f5f5f5', color: '#888' }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 3' }}>
                  <label style={{ fontSize: 11, color: '#666', fontWeight: 700, marginBottom: 2 }}>Descrição *</label>
                  <textarea
                    value={st.descricao}
                    onChange={e => {
                      const arr = [...servicosTerceirosOrc];
                      arr[index] = { ...arr[index], descricao: e.target.value };
                      setServicosTerceirosOrc(arr);
                    }}
                    placeholder="Descrição do serviço"
                    disabled={isReadOnly}
                    style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, minHeight: 60, resize: 'vertical' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setServicosTerceirosOrc([...servicosTerceirosOrc, { descricao: '', valor: '', fornecedorId: '', tipoServicoId: '', dataExecucao: '', status: 'Pendente' }]);
                  }}
                  disabled={isReadOnly}
                  style={{
                    padding: '10px 14px', backgroundColor: 'rgb(235, 133, 0)', color: 'white', border: 'none',
                    borderRadius: 6, cursor: 'pointer', fontSize: 18, fontWeight: 700, lineHeight: 1, minWidth: 40, height: 42
                  }}
                  title="Adicionar mais um serviço terceiro"
                >+</button>
                {/* Qualquer linha pode ser removida: o orçamento pode ficar sem serviço terceiro */}
                <button
                  type="button"
                  onClick={() => {
                    setServicosTerceirosOrc(servicosTerceirosOrc.filter((_, i) => i !== index));
                  }}
                  disabled={isReadOnly}
                  style={{
                    padding: '10px 14px', backgroundColor: '#f44336', color: 'white', border: 'none',
                    borderRadius: 6, cursor: 'pointer', fontSize: 18, fontWeight: 700, lineHeight: 1, minWidth: 40, height: 42
                  }}
                  title="Remover este serviço terceiro"
                >×</button>
              </div>
            </div>
          ))}
          {servicosTerceirosOrc.length === 0 && (
            <button
              type="button"
              onClick={() => {
                setServicosTerceirosOrc([{ descricao: '', valor: '', fornecedorId: '', tipoServicoId: '', dataExecucao: '', status: 'Pendente' }]);
              }}
              disabled={isReadOnly}
              style={{
                padding: '10px 14px', backgroundColor: 'rgb(235, 133, 0)', color: 'white', border: 'none',
                borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 700, height: 42
              }}
              title="Adicionar serviço terceiro"
            >+ Adicionar serviço terceiro</button>
          )}
        </div>

        {/* Seção Adicional de Lucro */}
        <div id="orc-secao-adicional-lucro" className="secao-formulario" style={{ border: '1px solid #c8e6c9', background: '#f1f8e9', padding: '20px', borderRadius: '8px' }}>
          <h3 style={{ color: '#2e7d32', margin: '0 0 10px 0' }}>💰 Adicional de Lucro</h3>
          <p style={{ fontSize: '13px', color: '#555', margin: '0 0 15px 0', lineHeight: '1.5' }}>
            Este valor representa a margem de lucro percentual que será aplicada sobre o custo base do orçamento.
            Após definir todos os custos (serviços terceiros, matérias-primas e responsáveis), insira aqui
            a porcentagem de lucro desejada. O sistema calculará automaticamente o valor do lucro e o preço
            final enviado ao cliente.
          </p>
          <div className="campo">
            <label style={{ fontWeight: 600 }}>Percentual de Lucro (%):</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <CampoComUnidade
                sufixo="%"
                corBorda="#a5d6a7"
                step="0.01"
                min="0"
                value={adicionalLucro}
                onChange={(e) => setAdicionalLucro(e.target.value)}
                disabled={loading}
                placeholder="10"
              />
              <span style={{ fontSize: 15, fontWeight: 700, color: '#2e7d32' }} title="Valor do lucro em reais">
                = {formatarReais(valorLucroCalculado)}
              </span>
            </div>
          </div>
        </div>

        {/* Seção Desconto (aplicado depois do adicional de lucro) */}
        <div id="orc-secao-desconto" className="secao-formulario" style={{ border: '1px solid #ffe0b2', background: '#fff8e1', padding: '20px', borderRadius: '8px', marginTop: '16px' }}>
          <h3 style={{ color: '#e65100', margin: '0 0 10px 0' }}>🏷️ Desconto</h3>

          {/* Forma de informar o desconto: em % sobre (custo + lucro) ou valor em R$ */}
          <div style={{ display: 'flex', gap: 24, marginBottom: 12, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={tipoDesconto === 'percentual'}
                onChange={() => {
                  if (tipoDesconto === 'percentual') return;
                  // Converte o valor atual em R$ para a porcentagem equivalente
                  const base = custoCalculado + valorLucroCalculado;
                  setDescontoPercentual(base > 0 ? String(arredondarValor((descontoReais / base) * 100)) : '');
                  setTipoDesconto('percentual');
                }}
                disabled={loading || isReadOnly}
              />
              Porcentagem (%)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={tipoDesconto === 'valor'}
                onChange={() => {
                  if (tipoDesconto === 'valor') return;
                  // Mantém o mesmo desconto, agora digitado em R$
                  handleFormChange('desconto', descontoReais);
                  setTipoDesconto('valor');
                }}
                disabled={loading || isReadOnly}
              />
              Valor (R$)
            </label>
          </div>

          <div className="campo">
            <label style={{ fontWeight: 600 }}>{tipoDesconto === 'percentual' ? 'Desconto (%):' : 'Desconto (R$):'}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {tipoDesconto === 'percentual' ? (
                <CampoComUnidade
                  sufixo="%"
                  corBorda="#ffcc80"
                  step="0.01"
                  min="0"
                  max="100"
                  value={descontoPercentual}
                  onChange={(e) => setDescontoPercentual(e.target.value)}
                  disabled={loading || isReadOnly}
                  placeholder="0"
                />
              ) : (
                <CampoComUnidade
                  prefixo="R$"
                  corBorda="#ffcc80"
                  step="0.01"
                  min="0"
                  value={form.desconto}
                  onChange={(e) => handleFormChange('desconto', e.target.value)}
                  disabled={loading || isReadOnly}
                  placeholder="0,00"
                />
              )}
              {tipoDesconto === 'percentual' && (
                <span style={{ fontSize: 15, fontWeight: 700, color: '#e65100' }} title="Valor do desconto em reais">
                  = {formatarReais(descontoReais)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Valor Total Repetido */}
        <div className="secao-formulario" style={{ border: '1px solid #e0e0e0', background: '#f5f5f5', padding: '20px', borderRadius: '8px', marginTop: '16px' }}>
          <h3 style={{ color: '#333', margin: '0 0 10px 0' }}>Valor Total</h3>
          <div className="campo">
            <label style={{ fontWeight: 600 }}>Valor Total (R$):</label>
            <input
              type="number"
              step="0.01"
              value={form.valor}
              readOnly
              disabled
              placeholder="R$ 0,00"
              style={{
                width: '200px',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '14px',
                backgroundColor: '#f5f5f5'
              }}
            />
          </div>
        </div>

        </div>{/* end fields wrapper */}

        {/* Mensagem de Erro (agora posicionada imediatamente acima dos botões de ação) */}
        {error && (
          <div 
            className="error-message" 
            style={{
              color: '#cc0000',
              margin: '10px 0 5px',
              textAlign: 'center',
              fontWeight: '600',
              background: '#ffe6e6',
              padding: '10px 15px',
              border: '1px solid #ffb3b3',
              borderRadius: '4px'
            }}
          >
            {error}
          </div>
        )}

        {/* Botões de Ação */}
        <div className="botoes-formulario" style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
          {orcamentoParaEditar && (
            <button 
              type="button"
              onClick={() => {
                if (window.confirm('Tem certeza que deseja cancelar? Todas as alterações serão perdidas.')) {
                  // Se existe uma função de callback para cancelar, chama ela
                  if (onCancelar) {
                    onCancelar();
                  } else {
                    // Caso contrário, apenas limpa o formulário
                    window.location.reload();
                  }
                }
              }}
              style={{
                background: '#6c757d', 
                color: 'white', 
                border: 'none', 
                padding: '15px 30px', 
                borderRadius: '5px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                minWidth: '150px',
                boxShadow: '0 2px 8px rgba(108, 117, 125, 0.3)',
                transition: 'all 0.3s'
              }}
              className="btn-cancelar-forte"
            >
              Cancelar
            </button>
          )}
          
          {!isReadOnly && (
          <button 
            type="submit" 
            disabled={loading || botaoDesabilitado}
            style={{
              background: (loading || botaoDesabilitado) ? '#ccc' : '#28a745', 
              color: 'white', 
              border: 'none', 
              padding: '15px 30px', 
              borderRadius: '5px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: (loading || botaoDesabilitado) ? 'not-allowed' : 'pointer',
              minWidth: '150px',
              boxShadow: (loading || botaoDesabilitado) ? 'none' : '0 2px 8px rgba(40, 167, 69, 0.3)',
              transition: 'all 0.3s',
              opacity: (loading || botaoDesabilitado) ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!loading && !botaoDesabilitado) e.target.style.background = '#218838';
            }}
            onMouseLeave={(e) => {
              if (!loading && !botaoDesabilitado) e.target.style.background = '#28a745';
            }}
          >
            {loading ? 'Salvando...' : (orcamentoParaEditar ? 'Atualizar Orçamento' : 'Salvar Orçamento')}
          </button>
          )}
          {orcamentoParaEditar && (
            <button
              type="button"
              onClick={() => setDuplicacao('confirmar')}
              disabled={loading}
              style={{
                background: '#f7931e',
                color: 'white',
                border: 'none',
                padding: '15px 30px',
                borderRadius: '5px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                minWidth: '150px',
                boxShadow: '0 2px 8px rgba(247, 147, 30, 0.3)',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#e0820f'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#f7931e'}
              title="Criar um orçamento novo idêntico a este"
            >
              Duplicar Orçamento
            </button>
          )}
          {orcamentoParaEditar && onCompartilhar && (
            <button
              type="button"
              onClick={() => {
                const orcId = orcamentoParaEditar.id || orcamentoParaEditar.Id;
                const clienteDoOrc = clientes.find(c => (c.id || c.Id) == form.id_cliente);
                const nomeCliente = clienteDoOrc ? (clienteDoOrc.nome || clienteDoOrc.Nome || '') : '';
                onCompartilhar(orcId, nomeCliente);
              }}
              style={{
                background: '#25D366',
                color: '#fff',
                border: 'none',
                padding: '15px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s',
                opacity: 1
              }}
              title="Compartilhar link de aprovação"
              className="btn-compartilhar"
              onMouseEnter={e => e.currentTarget.style.background = '#1EBE5A'}
              onMouseLeave={e => e.currentTarget.style.background = '#25D366'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            </button>
          )}
        </div>
      </form>

  {/* Popup removido: substituído por acordeão inline */}

      {duplicacao && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '16px' }}
          onClick={() => { if (duplicacao !== 'duplicando') fecharDuplicacao(); }}
        >
          <div
            style={{ background: '#fff', borderRadius: '12px', padding: '28px', maxWidth: '480px', width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', color: '#333' }}
            onClick={(e) => e.stopPropagation()}
          >
            {(duplicacao === 'confirmar' || duplicacao === 'duplicando') && (
              <>
                <h3 style={{ margin: '0 0 14px', color: '#f7931e' }}>Duplicar orçamento</h3>
                <p style={{ margin: '0 0 10px', lineHeight: 1.5 }}>
                  Será criado um <strong>orçamento novo</strong>, idêntico a este, copiando todos os dados:
                </p>
                <ul style={{ margin: '0 0 12px', paddingLeft: '20px', lineHeight: 1.6 }}>
                  <li>cliente e oportunidade</li>
                  <li>serviços, responsáveis e matérias-primas</li>
                  <li>serviços de terceiros e condições de pagamento</li>
                  <li>lucro, desconto e valores</li>
                </ul>
                <p style={{ margin: '0 0 20px', lineHeight: 1.5 }}>
                  O novo orçamento recebe um código novo e começa com status <strong>Rascunho</strong>, mesmo que este já esteja aceito. Este orçamento não será alterado.
                </p>
                <p style={{ margin: '0 0 20px', fontWeight: 600 }}>Tem certeza que deseja duplicar?</p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setDuplicacao(null)}
                    disabled={duplicacao === 'duplicando'}
                    style={{ background: '#6c757d', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '5px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Não
                  </button>
                  <button
                    type="button"
                    onClick={duplicarOrcamento}
                    disabled={duplicacao === 'duplicando'}
                    style={{ background: '#f7931e', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '5px', fontWeight: 600, cursor: duplicacao === 'duplicando' ? 'wait' : 'pointer' }}
                  >
                    {duplicacao === 'duplicando' ? 'Duplicando...' : 'Sim, duplicar'}
                  </button>
                </div>
              </>
            )}
            {typeof duplicacao === 'object' && duplicacao.id && (
              <>
                <h3 style={{ margin: '0 0 14px', color: '#28a745' }}>Orçamento duplicado</h3>
                <p style={{ margin: '0 0 20px', lineHeight: 1.5 }}>
                  O novo orçamento <strong>nº {duplicacao.id}</strong> foi criado com status Rascunho e já está na lista.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={fecharDuplicacao} style={{ background: '#28a745', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '5px', fontWeight: 600, cursor: 'pointer' }}>
                    OK
                  </button>
                </div>
              </>
            )}
            {typeof duplicacao === 'object' && duplicacao.erro && (
              <>
                <h3 style={{ margin: '0 0 14px', color: '#c0392b' }}>Não foi possível duplicar</h3>
                <p style={{ margin: '0 0 20px', lineHeight: 1.5 }}>{duplicacao.erro}</p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setDuplicacao(null)} style={{ background: '#6c757d', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '5px', fontWeight: 600, cursor: 'pointer' }}>
                    Fechar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
          15% { opacity: 1; transform: translateX(-50%) translateY(0); }
          85% { opacity: 1; transform: translateX(-50%) translateY(0); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
        }

        .formulario-orcamento {
          max-width: 1000px;
          margin: 0 auto;
          padding: 20px;
        }
        
        .secao-formulario {
          background: #f9f9f9;
          padding: 30px;
          margin-bottom: 25px;
          border-radius: 8px;
          border: 1px solid #e0e0e0;
        }
        
        .secao-formulario h3 {
          margin-top: 0;
          color: #333;
          border-bottom: 2px solid #007bff;
          padding-bottom: 10px;
          margin-bottom: 20px;
        }
        
        .linha-campos {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 15px;
        }
        
        .campo {
          margin-bottom: 15px;
        }
        
        .campo-completo {
          grid-column: 1 / -1;
        }
        
        .campo label {
          display: block;
          margin-bottom: 8px;
          font-weight: bold;
          color: #555;
          font-size: 14px;
        }
        
        .campo input,
        .campo select,
        .campo textarea {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          transition: border-color 0.3s;
        }
        
        .campo input:focus,
        .campo select:focus,
        .campo textarea:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 3px rgba(0,123,255,0.15);
        }
        
        .error-message {
          background: #f8d7da;
          color: #721c24;
          padding: 15px;
          border-radius: 6px;
          border: 1px solid #f5c6cb;
          margin-bottom: 20px;
        }
        
        .botoes-formulario {
          text-align: center;
          margin-top: 30px;
        }
        
        .botoes-formulario button {
          border: none;
          padding: 15px 30px;
          border-radius: 6px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          min-width: 150px;
        }
        
        .botoes-formulario button[type="submit"] {
          background: #28a745 !important;
          color: white !important;
          box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
        }
        
        .botoes-formulario button[type="submit"]:hover {
          background: #218838 !important;
        }
        
        .botoes-formulario button[type="submit"]:disabled {
          background: #ccc !important;
          cursor: not-allowed;
        }
        
        .botoes-formulario button[type="button"] {
          background: #6c757d !important;
          color: white !important;
          box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
        }
        
        .botoes-formulario button[type="button"]:hover {
          background: #5a6268 !important;
        }

        .botoes-formulario button.btn-compartilhar {
          background: #25D366 !important;
          box-shadow: none !important;
        }

        .botoes-formulario button.btn-compartilhar:hover {
          background: #1EBE5A !important;
        }

        @media (max-width: 768px) {
          .formulario-orcamento {
            padding: 16px;
          }

          .secao-formulario {
            padding: 20px 16px;
            margin-bottom: 16px;
          }

          .secao-formulario h3 {
            font-size: 18px;
          }

          .linha-campos {
            grid-template-columns: 1fr !important;
            gap: 12px;
          }

          .campo input,
          .campo select,
          .campo textarea {
            font-size: 16px;
            padding: 10px 12px;
          }

          .botoes-formulario {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }

          .botoes-formulario button {
            width: 100%;
            min-width: unset;
            padding: 14px 20px;
          }
        }
      `}</style>
    </div>
  );
}

export default FormularioOrcamento;
